import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Users, Clock, Eye, Loader2, Search, Pencil, Star, Plus,
  CircleX, CircleCheckBig, CircleCheck, CircleAlert, Minus,
  ChevronUp, ChevronDown, X, ArrowRight, Download, ShieldCheck,
  SlidersHorizontal, Info,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { toast, Toaster } from '../../components/Toast';
import {
  listPipelineCandidates,
  approveProfiles,
  rejectProfiles,
  moveToReview,
  createPacket,
  addToPacket,
  bulkEditCandidates,
  setCandidateGolden,
  setCandidatesGoldenBulk,
  getFieldOptions,
  type PipelineCandidate,
  type PacketSummary,
  type SearchDomain,
} from '../../services/api';
import { ckey, formatDate, getInitials } from './constants';
import { CandidateUnifiedModal, type DetailTab } from './CandidateUnifiedModal';
import { useAdminData } from './AdminDataContext';
import { AdminSelect } from './AdminSelect';
import { getCached, setCache, invalidateCachePrefix } from '../../utils/apiCache';
import { exportPipelineCandidatesXlsx } from '../../utils/exportPipelineCandidatesXlsx';
import { useFilterPanelPlacement, SortFilterHeader } from './SharedTableComponents';
import AdminFilterSection from './AdminFilterSection';
import { SearchFilters, AppliedFilters } from '../../types';
import { fetchAppConfig } from '../../config/appConfig';

export type Tab = 'candidates' | 'in-review' | 'rejected';
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 500] as const;
const DEFAULT_PAGE_SIZE = 20;

function VerificationIcon({ value }: { value: string | null }) {
  const v = (value ?? '').toUpperCase();
  if (v === 'YES') return <CircleCheckBig className="w-4 h-4 text-green-500" />;
  if (v === 'NO') return <CircleX className="w-4 h-4 text-red-500" />;
  if (v === 'NOT_FOUND') return <CircleAlert className="w-4 h-4 text-amber-500" />;
  if (v === 'PARTIALLY_FOUND') return <CircleAlert className="w-4 h-4 text-orange-500" />;
  return <Minus className="w-4 h-4 text-gray-400" />;
}

function PageSizeSelector({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        {value}
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute bottom-full mb-1 right-0 min-w-[4.5rem] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-50">
          {PAGE_SIZE_OPTIONS.map((opt) => (
            <button
              key={opt}
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                opt === value
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-medium'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ScrollButton() {
  const [dir, setDir] = useState<'up' | 'down' | null>(null);
  const lastY = useRef(window.scrollY);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const maxY = document.documentElement.scrollHeight - window.innerHeight;
      if (maxY < 200) { setDir(null); return; }

      const scrollingDown = y > lastY.current;
      lastY.current = y;

      if (scrollingDown && y < maxY - 50) setDir('down');
      else if (!scrollingDown && y > 50) setDir('up');
      else { setDir(null); return; }

      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setDir(null), 2000);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (timer.current) clearTimeout(timer.current); };
  }, []);

  if (!dir) return null;

  return (
    <div className="fixed bottom-8 right-8 z-10 animate-in fade-in slide-in-from-bottom-2 duration-200">
      <button
        onClick={() => {
          window.scrollTo({ top: dir === 'up' ? 0 : document.documentElement.scrollHeight, behavior: 'smooth' });
          setDir(null);
        }}
        className="p-2.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg text-gray-500 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        title={dir === 'up' ? 'Scroll to top' : 'Scroll to bottom'}
      >
        {dir === 'up' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
    </div>
  );
}

function Pagination({ page, totalPages, total, pageSize, onPageChange, onPageSizeChange }: { page: number; totalPages: number; total: number; pageSize: number; onPageChange: (p: number) => void; onPageSizeChange: (size: number) => void }) {
  if (total === 0) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm mt-4">
      <div className="text-sm text-gray-500">
        Showing <span className="font-medium text-gray-900 dark:text-gray-100">{from}</span> to <span className="font-medium text-gray-900 dark:text-gray-100">{to}</span> of <span className="font-medium text-gray-900 dark:text-gray-100">{total}</span>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Per page</span>
          <PageSizeSelector value={pageSize} onChange={onPageSizeChange} />
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <ChevronUp className="w-5 h-5 -rotate-90" />
            </button>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 px-2">Page {page} of {totalPages}</div>
            <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <ChevronDown className="w-5 h-5 -rotate-90" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterChips({ filters, labels, onRemove, onClearAll, sort, sortLabels, onRemoveSort, search, onRemoveSearch }: {
  filters: Record<string, string[] | undefined>;
  labels: Record<string, string>;
  onRemove: (key: string) => void;
  onClearAll: () => void;
  sort?: { col: string; dir: 'asc' | 'desc' } | null;
  sortLabels?: Record<string, string>;
  onRemoveSort?: () => void;
  search?: string;
  onRemoveSearch?: () => void;
}) {
  const active = Object.entries(filters).filter(([, v]) => v && v.length > 0);
  const hasSort = sort && onRemoveSort;
  const hasSearch = search && onRemoveSearch;
  if (active.length === 0 && !hasSort && !hasSearch) return null;
  return (
    <div className="px-4 pt-3 pb-3 flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-gray-500">Active filters:</span>
      {hasSearch && (
        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border border-purple-100 dark:border-purple-800">
          <span className="font-medium">Search:</span>
          <span className="inline-block max-w-[260px] truncate align-bottom">{search}</span>
          <button onClick={onRemoveSearch} className="p-0.5 hover:bg-purple-100 dark:hover:bg-purple-800 rounded text-purple-500 hover:text-purple-800 transition-colors">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      {hasSort && (
        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-100 dark:border-green-800">
          <span className="font-medium">Sort:</span>
          <span>{sortLabels?.[sort!.col] || sort!.col} ({sort!.dir})</span>
          <button onClick={onRemoveSort} className="p-0.5 hover:bg-green-100 dark:hover:bg-green-800 rounded text-green-500 hover:text-green-800 transition-colors">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      {active.map(([key, values]) => (
        <div key={key} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-800">
          <span className="font-medium">{labels[key] || key}:</span>
          <span>{values!.map((v) => v === 'NOT_FOUND' ? 'Not found' : v === 'PARTIALLY_FOUND' ? 'Partially found' : v === 'YES' ? 'Yes' : v === 'NO' ? 'No' : v).join(', ')}</span>
          <button onClick={() => onRemove(key)} className="p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800 rounded text-blue-500 hover:text-blue-800 transition-colors">
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
      <button onClick={onClearAll} className="text-xs text-gray-500 hover:text-gray-700 underline ml-2">Clear all</button>
    </div>
  );
}

// A comma/newline in the box = a pasted talent-ID list. Split on any separator run,
// drop blanks and punctuation-only junk (stray "." / trailing commas), dedupe. Must
// match the backend split so the guard count == what the query actually runs.
const idTokens = (s: string): string[] =>
  /[,\n]/.test(s)
    ? Array.from(new Set(s.split(/[\s,;]+/).map((t) => t.trim()).filter((t) => /[A-Za-z0-9]/.test(t))))
    : [];

function SearchBar({ value, onChange, placeholder, rightSlot }: { value: string; onChange: (v: string) => void; placeholder?: string; rightSlot?: React.ReactNode }) {
  return (
    <div className="p-4 bg-gray-50/50 dark:bg-gray-800/30 border-t border-gray-200 dark:border-gray-800">
      <div className="flex items-start gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <textarea
            rows={1}
            placeholder={placeholder || 'Search by name or talent ID...'}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
            className="w-full pl-9 pr-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow resize-none"
          />
        </div>
        {rightSlot}
      </div>
    </div>
  );
}

// Applied advanced (ES) filter → chip label. `domain` is the panel selector, not a chip.
const ADV_LABELS: Record<string, string> = {
  searchQuery: 'Query', companies: 'Companies', currentCompanies: 'Current company', pastCompanies: 'Previous company',
  skills: 'Skills', education: 'Education', school: 'School', countries: 'Location', usState: 'US State', yearOfExperience: 'Experience',
  hourlyRate: 'Rate', certificates: 'Certificates', researchPapersCount: 'Research papers', naturalLanguages: 'Languages',
  programmingLanguages: 'Prog. languages', typeOfWork: 'Expertise', field: 'Field', domainFilter: 'Domain filter',
  hasGithubData: 'GitHub data', titles: 'Titles', countriesExclude: 'Exclude location', companiesExclude: 'Exclude company',
  skillsExclude: 'Exclude skills',
};
const advHasContent = (x: unknown): boolean =>
  x != null && (typeof x === 'object'
    ? Object.values(x as Record<string, unknown>).some((y) => y != null && String(y).trim() !== '')
    : String(x).trim() !== '');
const advIsActive = (v: unknown): boolean => {
  if (v == null) return false;
  // Arrays (incl. skills = [{name}]) are active only if some entry has real content —
  // a stray [{name:''}] must not count.
  if (Array.isArray(v)) return v.some(advHasContent);
  if (typeof v === 'object') return Object.values(v as Record<string, unknown>).some((x) => x != null);
  if (typeof v === 'string') return v.trim() !== '';
  return !!v;
};
const advValueText = (key: string, v: unknown): string => {
  if (Array.isArray(v)) {
    if (key === 'skills' || key === 'skillsExclude') return v.map((s: any) => s?.name ?? s).filter((n: any) => String(n ?? '').trim()).join(', ');
    return v.filter((n: any) => String(n ?? '').trim()).join(', ');
  }
  if (key === 'yearOfExperience' || key === 'hourlyRate') {
    const r = v as { min?: number; max?: number };
    return `${r.min ?? 0}–${r.max ?? '∞'}`;
  }
  if (key === 'hasGithubData') return 'Yes';
  if (key === 'researchPapersCount') return `≥ ${v}`;
  return String(v);
};

function AddToBatchModal({ ids, onClose, onDone }: { ids: number[]; onClose: () => void; onDone: () => void }) {
  const { packets: contextPackets } = useAdminData();
  const [packets, setPackets] = useState<PacketSummary[]>(contextPackets);
  const [batchName, setBatchName] = useState('');
  const [selectedPacketId, setSelectedPacketId] = useState<number | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setPackets(contextPackets); }, [contextPackets]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      let packetId = selectedPacketId;
      if (!packetId && batchName.trim()) {
        const p = await createPacket(batchName.trim());
        packetId = p.id;
      }
      if (packetId) {
        await addToPacket(packetId, ids);
        onDone();
      }
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add to Batch</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Batch Name</label>
            <div className="relative">
              <input
                type="text"
                placeholder="e.g. Python devs from India"
                value={batchName}
                onChange={(e) => { setBatchName(e.target.value); setSelectedPacketId(null); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
              {showDropdown && (
                <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
                  <div className="max-h-40 overflow-y-auto">
                    {batchName.trim() && !packets.some((p) => p.name.toLowerCase() === batchName.trim().toLowerCase()) && (
                      <button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setSelectedPacketId(null); setShowDropdown(false); }}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors border-b border-gray-100 dark:border-gray-700"
                      >
                        <span className="font-medium text-blue-600 dark:text-blue-400">Create &quot;{batchName.trim()}&quot;</span>
                        <div className="text-xs text-gray-500 mt-0.5">New batch</div>
                      </button>
                    )}
                    {packets.filter((p) => !batchName || p.name.toLowerCase().includes(batchName.toLowerCase())).map((p) => (
                      <button
                        key={p.id}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setSelectedPacketId(p.id); setBatchName(p.name); setShowDropdown(false); }}
                        className={`w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${selectedPacketId === p.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                      >
                        <span className="font-medium text-gray-900 dark:text-gray-100">{p.name}</span>
                        <div className="text-xs text-gray-500 mt-0.5">{p.candidatesCount} candidates</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || (!selectedPacketId && !batchName.trim())}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add to Batch'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function BulkEditModal({ candidates, onClose, onDone }: { candidates: PipelineCandidate[]; onClose: () => void; onDone: () => void }) {
  const { searchDomains } = useAdminData();
  const [domains, setDomains] = useState<SearchDomain[]>(searchDomains);
  const [newDomain, setNewDomain] = useState('');
  const [newField, setNewField] = useState('');
  const [fieldOptions, setFieldOptions] = useState<string[]>([]);
  const [showFieldDropdown, setShowFieldDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [result, setResult] = useState<{ updated: number; skipped: { talentId: string; reason: string }[] } | null>(null);

  useEffect(() => { setDomains(searchDomains); }, [searchDomains]);

  const targetDomainKey = newDomain || (candidates.length === 1 ? candidates[0].domain.toLowerCase() : '');
  useEffect(() => {
    if (!targetDomainKey) { setFieldOptions([]); return; }
    getFieldOptions(targetDomainKey).then(setFieldOptions).catch(() => setFieldOptions([]));
  }, [targetDomainKey]);

  const includedCandidates = candidates.filter((c) => !excludedIds.has(c.talentId));

  const handleConfirm = async () => {
    if (includedCandidates.length === 0) return;
    setSaving(true);
    try {
      const res = await bulkEditCandidates({
        selections: includedCandidates.map((c) => ({ talentId: c.talentId, currentDomain: c.domain.toLowerCase() })),
        newDomain: newDomain || undefined,
        newField: newField || undefined,
      });
      setResult(res);
      if (res.updated > 0) setTimeout(() => onDone(), 4000);
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  const toggleExclude = (talentId: string) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(talentId)) next.delete(talentId);
      else next.add(talentId);
      return next;
    });
  };

  const toggleGroup = (groupIndex: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupIndex)) next.delete(groupIndex);
      else next.add(groupIndex);
      return next;
    });
  };

  const domainLabel = (key: string) => domains.find((d) => d.key === key)?.label || key;

  const confirmGroups = React.useMemo(() => {
    const groups = new Map<string, { domain: string; field: string; members: PipelineCandidate[] }>();
    candidates.forEach((c) => {
      const key = `${c.domain.toLowerCase()}::${c.field || ''}`;
      const g = groups.get(key);
      if (g) g.members.push(c);
      else groups.set(key, { domain: c.domain.toLowerCase(), field: c.field || '', members: [c] });
    });
    return Array.from(groups.values()).sort((a, b) => b.members.length - a.members.length);
  }, [candidates]);

  const filteredFieldOpts = fieldOptions.filter((f) => !newField || f.toLowerCase().includes(newField.toLowerCase()));

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className={`bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full animate-in zoom-in-95 duration-200 transition-all ${confirming ? 'max-w-2xl' : 'max-w-md'}`} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{confirming ? 'Confirm Changes' : `Bulk Edit (${candidates.length} profiles)`}</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {result ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-600">
                <CircleCheck className="w-5 h-5" />
                <span className="font-medium">Updated {result.updated} profile{result.updated !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-start gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <Clock className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-300">Changes will be reflected in search results after the next sync cycle (~15 minutes).</p>
              </div>
              {result.skipped.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <div className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-1">Skipped {result.skipped.length} profile{result.skipped.length !== 1 ? 's' : ''}:</div>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {result.skipped.map((s, i) => (
                      <div key={i} className="text-xs text-amber-700 dark:text-amber-400">{s.talentId}: {s.reason}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : confirming ? (
            <div className="space-y-3">
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {confirmGroups.map((g, i) => {
                  const domainChanging = newDomain && newDomain !== g.domain;
                  // Explicit new field, OR the field is cleared to none because a
                  // domain was (re)assigned and drops the previous field — even
                  // when the group already sits in the chosen domain (no-op move).
                  const fieldCleared = !!newDomain && !newField && !!g.field;
                  const fieldChanging = (newField && newField !== g.field) || fieldCleared;
                  const groupIncluded = g.members.filter((m) => !excludedIds.has(m.talentId));
                  const allIncluded = groupIncluded.length === g.members.length;
                  const noneIncluded = groupIncluded.length === 0;
                  const expanded = expandedGroups.has(i);
                  return (
                    <div key={i} className={`border rounded-lg overflow-hidden transition-colors ${noneIncluded ? 'border-gray-200 dark:border-gray-800 opacity-40' : 'border-gray-200 dark:border-gray-700'}`}>
                      <div
                        className="bg-gray-50 dark:bg-gray-800 px-3 py-2 flex items-center gap-2 cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                        onClick={() => toggleGroup(i)}
                      >
                        <input
                          type="checkbox"
                          checked={!noneIncluded}
                          ref={(el) => { if (el) el.indeterminate = !allIncluded && !noneIncluded; }}
                          onChange={(e) => {
                            e.stopPropagation();
                            setExcludedIds((prev) => {
                              const next = new Set(prev);
                              if (allIncluded) g.members.forEach((m) => next.add(m.talentId));
                              else g.members.forEach((m) => next.delete(m.talentId));
                              return next;
                            });
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer shrink-0"
                        />
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 shrink-0">
                          {groupIncluded.length}/{g.members.length}
                        </span>
                        <div className="flex items-center gap-1.5 flex-1 min-w-0 flex-wrap">
                          {domainChanging ? (
                            <>
                              <span className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 truncate max-w-[100px]">{domainLabel(g.domain)}</span>
                              <ArrowRight className="w-3 h-3 text-gray-400 shrink-0" />
                              <span className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800 truncate max-w-[100px]">{domainLabel(newDomain)}</span>
                            </>
                          ) : newDomain ? (
                            <span className="text-[11px] text-gray-400 italic">domain unchanged</span>
                          ) : null}
                          {newDomain && (fieldChanging || newField) && <span className="text-gray-300 dark:text-gray-600">|</span>}
                          {fieldChanging ? (
                            <>
                              <span className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 truncate max-w-[100px]">{g.field || '(none)'}</span>
                              <ArrowRight className="w-3 h-3 text-gray-400 shrink-0" />
                              <span className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800 truncate max-w-[100px]">{newField || '(none)'}</span>
                            </>
                          ) : newField ? (
                            <span className="text-[11px] text-gray-400 italic">field unchanged</span>
                          ) : null}
                        </div>
                        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                      </div>
                      {expanded && (
                        <div className="border-t border-gray-100 dark:border-gray-800 divide-y divide-gray-50 dark:divide-gray-800/50">
                          {g.members.map((m) => {
                            const included = !excludedIds.has(m.talentId);
                            return (
                              <div
                                key={m.talentId}
                                className={`flex items-center gap-2 px-3 py-1.5 text-xs transition-colors ${included ? 'hover:bg-gray-50 dark:hover:bg-gray-800/30' : 'opacity-35'}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={included}
                                  onChange={() => toggleExclude(m.talentId)}
                                  className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer shrink-0"
                                />
                                <span className="font-mono text-gray-500 dark:text-gray-400 shrink-0 w-24 truncate">{m.talentId}</span>
                                <span className="text-gray-900 dark:text-gray-100 truncate flex-1">{m.name || '—'}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {excludedIds.size > 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
                  {excludedIds.size} excluded · <button onClick={() => setExcludedIds(new Set())} className="text-blue-600 dark:text-blue-400 hover:underline">reset</button>
                </div>
              )}
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Domain</label>
                <AdminSelect
                  value={newDomain}
                  onChange={(val) => { setNewDomain(val); setNewField(''); }}
                  options={[
                    { value: '', label: '— No change —' },
                    ...domains.filter((d) => d.isActive).map((d) => ({ value: d.key, label: d.label, sublabel: d.key })),
                  ]}
                  placeholder="— No change —"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Field</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder={targetDomainKey ? 'Search fields...' : 'Select domain first to see field options'}
                    value={newField}
                    onChange={(e) => { setNewField(e.target.value); setShowFieldDropdown(true); }}
                    onFocus={() => setShowFieldDropdown(true)}
                    onBlur={() => setTimeout(() => setShowFieldDropdown(false), 150)}
                    disabled={!targetDomainKey && !newDomain}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm disabled:opacity-50"
                  />
                  {showFieldDropdown && (
                    <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
                      <div className="max-h-40 overflow-y-auto">
                        {filteredFieldOpts.length > 0 ? filteredFieldOpts.map((opt) => (
                          <button
                            key={opt}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { setNewField(opt); setShowFieldDropdown(false); }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${newField === opt ? 'bg-blue-50 dark:bg-blue-900/20 font-medium' : ''}`}
                          >{opt}</button>
                        )) : newField.trim() ? (
                          <div className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400">
                            No matching field found. You can add new fields for specific tenants in <span className="font-medium text-gray-700 dark:text-gray-300">Account Management</span>.
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
                {newField && !fieldOptions.includes(newField) && !showFieldDropdown && (
                  <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
                    <CircleAlert className="w-3 h-3 inline mr-1" />
                    "{newField}" is not a configured field. Please add this field for the desired tenant in Account Management first.
                  </p>
                )}
                {newField && fieldOptions.includes(newField) && (
                  <button onClick={() => setNewField('')} className="mt-1 text-xs text-gray-500 hover:text-gray-700 underline">Clear field</button>
                )}
              </div>
            </>
          )}
        </div>
        {!result && (
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
            {confirming ? (
              <>
                <button onClick={() => setConfirming(false)} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  Back
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={saving || includedCandidates.length === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Confirm & Update ${includedCandidates.length} profile${includedCandidates.length !== 1 ? 's' : ''}`}
                </button>
              </>
            ) : (
              <>
                <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  Cancel
                </button>
                <button
                  onClick={() => setConfirming(true)}
                  disabled={(!newDomain && !newField) || (!!newField && !fieldOptions.includes(newField))}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Review Changes
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Shown while a filter/sort/page fetch is in flight. Floats over the rows only —
 * the toolbar, column headers (with their filter controls) and pagination stay
 * mounted so a filter click never makes the controls disappear under the cursor.
 */
function TableLoadingOverlay({ containerRef }: { containerRef: React.RefObject<HTMLDivElement | null> }) {
  const [band, setBand] = useState<{ top: number; left: number; width: number } | null>(null);

  // Centre in the visible slice of the rows area: intersect the table container with
  // the viewport, drop the header row off the top, then take the middle of what's
  // left. Plain `absolute` centring lands off-screen on a table taller than the
  // viewport, and `sticky` is unavailable because an ancestor card sets
  // `overflow-hidden`, which makes it the scrollport.
  useEffect(() => {
    const compute = () => {
      const el = containerRef.current;
      if (!el) { setBand(null); return; }
      const rect = el.getBoundingClientRect();
      const headHeight = el.querySelector('thead')?.getBoundingClientRect().height ?? 44;
      const top = Math.max(rect.top + headHeight, 0);
      const bottom = Math.min(rect.bottom, window.innerHeight);
      const left = Math.max(rect.left, 0);
      const right = Math.min(rect.right, window.innerWidth);
      if (bottom - top < 40 || right - left < 40) { setBand(null); return; }
      setBand({ top: (top + bottom) / 2, left, width: right - left });
    };
    compute();
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [containerRef]);

  if (!band) return null;

  return (
    // z-30 keeps this under the detail modal (z-50).
    <div
      className="pointer-events-none fixed z-30 flex justify-center"
      style={{ top: band.top, left: band.left, width: band.width, transform: 'translateY(-50%)' }}
    >
      <div className="flex flex-col items-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 px-8 py-6 shadow-lg">
        <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
        <span className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading candidates...</span>
      </div>
    </div>
  );
}

const NESTED_FILTER_PANEL_WIDTH = 320; // matches w-80

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function SortHeader({ label, sortKey, currentSort, onSort, className }: {
  label: string; sortKey: string;
  currentSort: { col: string; dir: 'asc' | 'desc' } | null;
  onSort: (col: string) => void;
  className?: string;
}) {
  const isActive = currentSort?.col === sortKey;
  return (
    <th className={`px-4 py-3 font-semibold select-none ${className || ''}`}>
      <div className={`flex items-center gap-1.5 ${className?.includes('text-center') ? 'justify-center' : ''}`}>
        <div className="flex items-center gap-1.5 cursor-pointer hover:text-gray-900 dark:hover:text-gray-100" onClick={() => onSort(sortKey)}>
          {label}
          <div className="flex flex-col">
            <ChevronUp className={`w-2 h-2 -mb-0.5 ${isActive && currentSort?.dir === 'asc' ? 'text-blue-600' : 'text-gray-300 dark:text-gray-600'}`} />
            <ChevronDown className={`w-2 h-2 ${isActive && currentSort?.dir === 'desc' ? 'text-blue-600' : 'text-gray-300 dark:text-gray-600'}`} />
          </div>
        </div>
      </div>
    </th>
  );
}

type FilterSub = { key: string; label: string; allowNotFound?: boolean; options?: ('YES' | 'NO' | 'NOT_FOUND' | 'PARTIALLY_FOUND')[] };
type FilterGroup = { groupLabel?: string; subs: FilterSub[] };

// ID Verification: column-level Yes/No filter + per-sub binary filters.
const ID_VERIFICATION_GROUPS: FilterGroup[] = [
  {
    groupLabel: 'Overall',
    subs: [
      { key: 'linkedinVerified', label: 'ID Verification', options: ['YES', 'NO'] },
    ],
  },
  {
    groupLabel: 'Yes',
    subs: [
      { key: 'idvSubWorkVerified', label: 'Work Verified' },
      { key: 'idvSubEduVerified', label: 'Edu Verified' },
      { key: 'idvSubIdVerified', label: 'Id Verified' },
    ],
  },
  {
    groupLabel: 'No',
    subs: [
      { key: 'idvSubLinkedinFound', label: 'LinkedIn found' },
      { key: 'idvSubAccountUnder3y', label: 'LinkedIn found - account <3 yrs' },
      { key: 'idvSubAccountOver3y', label: 'LinkedIn found - account >3 yrs' },
    ],
  },
];

// Public Source Check: column-level 4-state filter + per-sub tri-state filters.
const PUBLIC_SOURCE_CHECK_GROUPS: FilterGroup[] = [
  {
    groupLabel: 'Overall',
    subs: [
      { key: 'webAgentVerification', label: 'Public Source Check', options: ['YES', 'NO', 'NOT_FOUND', 'PARTIALLY_FOUND'] },
    ],
  },
  {
    groupLabel: 'Sub-sections',
    subs: [
      { key: 'pscSubAgentVerdict', label: 'Agent verdict', allowNotFound: true },
      { key: 'pscSubEducation', label: 'Education verification', allowNotFound: true },
      { key: 'pscSubWork', label: 'Work verification', allowNotFound: true },
      { key: 'pscSubGithub', label: 'GitHub/Portfolio verification', allowNotFound: true },
      { key: 'pscSubDisqualifiers', label: 'Disqualifiers', allowNotFound: true },
    ],
  },
];

type SubFilterState = Record<string, string[] | undefined>;

function NestedFilterDropdown({ groups, filterState, onChange }: { groups: FilterGroup[]; filterState: SubFilterState; onChange: (key: string, value: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const dropRef = React.useRef<HTMLDivElement>(null);
  const pos = useFilterPanelPlacement(open, btnRef, NESTED_FILTER_PANEL_WIDTH, 'right');

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allSubs = groups.flatMap((g) => g.subs);
  const activeCount = allSubs.reduce((n, s) => n + (filterState[s.key]?.length ? 1 : 0), 0);
  const anyActive = activeCount > 0;

  const toggle = (key: string, opt: string) => {
    const cur = filterState[key] ?? [];
    const next = cur.includes(opt) ? cur.filter((v) => v !== opt) : [...cur, opt];
    onChange(key, next);
  };

  const clearAll = () => {
    for (const s of allSubs) {
      if ((filterState[s.key]?.length ?? 0) > 0) onChange(s.key, []);
    }
  };

  return (
    <span className="inline-block ml-1">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(!open)}
        aria-label="Filter"
        className={`inline-flex items-center gap-1 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${anyActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-300 dark:text-gray-600 hover:text-gray-500'}`}
      >
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z" /></svg>
        {anyActive && (
          <span className="px-1 min-w-[14px] rounded-full bg-blue-600 text-white text-[9px] leading-[14px] font-semibold normal-case">{activeCount}</span>
        )}
      </button>
      {open && ReactDOM.createPortal(
        <div
          ref={dropRef}
          className="fixed w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg font-normal text-left text-sm flex flex-col overflow-hidden"
          style={{ top: pos.top, left: pos.left, maxHeight: pos.maxHeight, zIndex: 9999 }}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); btnRef.current?.focus(); } }}
        >
          <div className="flex-1 overflow-y-auto py-1">
          {groups.map((g, gi) => (
            <div key={g.groupLabel ?? gi} className="border-t border-gray-100 dark:border-gray-800 first:border-t-0">
              {g.groupLabel && (
                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
                  {g.groupLabel}
                </div>
              )}
              {g.subs.map((s) => {
                const sel = filterState[s.key] ?? [];
                const opts: ('YES' | 'NO' | 'NOT_FOUND' | 'PARTIALLY_FOUND')[] = s.options ?? (s.allowNotFound ? ['YES', 'NO', 'NOT_FOUND'] : ['YES', 'NO']);
                const optLabel = (opt: string) => opt === 'NOT_FOUND' ? 'Not found' : opt === 'PARTIALLY_FOUND' ? 'Partial' : opt === 'YES' ? 'Yes' : 'No';
                return (
                  <div key={s.key} className="px-3 py-1.5 flex items-center justify-between gap-2">
                    <div className="text-xs text-gray-700 dark:text-gray-300 truncate" title={s.label}>{s.label}</div>
                    <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                      {opts.map((opt) => {
                        const checked = sel.includes(opt);
                        return (
                          <button
                            key={opt}
                            onClick={() => toggle(s.key, opt)}
                            className={`px-2 py-0.5 rounded text-[10px] font-medium border transition-colors ${checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                          >
                            {optLabel(opt)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          </div>
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-800/40">
            <span className="text-[11px] text-gray-500 dark:text-gray-400 normal-case">
              {anyActive ? `${activeCount} filter${activeCount === 1 ? '' : 's'} active` : 'No filters'}
            </span>
            <button
              type="button"
              onClick={clearAll}
              disabled={!anyActive}
              className="text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:text-gray-300 dark:disabled:text-gray-600 disabled:no-underline disabled:cursor-default normal-case"
            >
              Clear all
            </button>
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}

function PublicSourceCheckCell({ value, sub }: { value: string | null; sub?: import('../../services/api').PublicSourceCheckSub }) {
  const [open, setOpen] = useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (open && wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: Math.max(8, rect.left - 100) });
    }
  }, [open]);

  if (!sub) {
    return <div className="flex justify-center"><VerificationIcon value={value} /></div>;
  }

  const rows: { label: string; state: 'YES' | 'NO' | 'NOT_FOUND' }[] = [
    { label: 'Agent verdict', state: sub.agentVerdict },
    { label: 'Education verification', state: sub.educationVerification },
    { label: 'Work verification', state: sub.workVerification },
    { label: 'GitHub/Portfolio verification', state: sub.githubVerification },
    { label: 'Disqualifiers Passed', state: sub.disqualifiers },
  ];

  const stateBadge = (state: string) => {
    if (state === 'YES') return <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">Yes</span>;
    if (state === 'NO') return <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">No</span>;
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">Not found</span>;
  };

  return (
    <div ref={wrapRef} className="flex justify-center relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <VerificationIcon value={value} />
      {open && ReactDOM.createPortal(
        <div className="fixed w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 font-normal text-left text-xs pointer-events-none" style={{ top: pos.top, left: pos.left, zIndex: 9999 }}>
          {rows.map((r) => (
            <div key={r.label} className="px-3 py-1 flex items-center justify-between gap-2">
              <span className="text-gray-700 dark:text-gray-300 truncate">{r.label}</span>
              {stateBadge(r.state)}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

function IdVerificationCell({ value, sub }: { value: string | null; sub?: import('../../services/api').IdVerificationSub }) {
  const [open, setOpen] = useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (open && wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: Math.max(8, rect.left - 100) });
    }
  }, [open]);

  if (!sub) {
    return <div className="flex justify-center"><VerificationIcon value={value} /></div>;
  }

  const rows: { group: 'YES' | 'NO'; label: string; state: 'YES' | 'NO' }[] = [
    { group: 'YES', label: 'Work Verified', state: sub.workVerified },
    { group: 'YES', label: 'Edu Verified', state: sub.eduVerified },
    { group: 'YES', label: 'Id Verified', state: sub.idVerified },
    { group: 'NO', label: 'LinkedIn found', state: sub.linkedinFound },
    { group: 'NO', label: 'LinkedIn found - account <3 yrs', state: sub.linkedinAccountUnder3y },
    { group: 'NO', label: 'LinkedIn found - account >3 yrs', state: sub.linkedinAccountOver3y },
  ];

  const stateBadge = (state: string) => {
    if (state === 'YES') return <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">Yes</span>;
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">No</span>;
  };

  return (
    <div ref={wrapRef} className="flex justify-center relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <VerificationIcon value={value} />
      {open && ReactDOM.createPortal(
        <div className="fixed w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 font-normal text-left text-xs pointer-events-none" style={{ top: pos.top, left: pos.left, zIndex: 9999 }}>
          {(['YES', 'NO'] as const).map((g) => (
            <div key={g} className="border-t border-gray-100 dark:border-gray-800 first:border-t-0">
              <div className="px-3 pt-1.5 pb-0.5 text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
                {g === 'YES' ? 'Yes' : 'No'}
              </div>
              {rows.filter((r) => r.group === g).map((r) => (
                <div key={r.label} className="px-3 py-1 flex items-center justify-between gap-2">
                  <span className="text-gray-700 dark:text-gray-300 truncate">{r.label}</span>
                  {stateBadge(r.state)}
                </div>
              ))}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

/** Filter keys shared by the two review-shaped tabs (In Review and Rejected). */
type ReviewFilters = {
  domain?: string[]; field?: string[]; groupName?: string[]; addedBy?: string[];
  golden?: string[];
  linkedinVerified?: string[]; resumeVerification?: string[]; webAgentVerification?: string[];
  agentRecommendation?: string[]; reviewTags?: string[];
  idvSubWorkVerified?: string[]; idvSubEduVerified?: string[]; idvSubIdVerified?: string[];
  idvSubLinkedinFound?: string[]; idvSubAccountUnder3y?: string[]; idvSubAccountOver3y?: string[];
  pscSubEducation?: string[]; pscSubWork?: string[]; pscSubGithub?: string[];
  pscSubDisqualifiers?: string[]; pscSubAgentVerdict?: string[];
};

type ReviewFilterOpts = { domains: string[]; fields: string[]; groups: string[]; addedBys: string[]; reviewTags: string[] };

/**
 * Maps review-tab filter state onto listPipelineCandidates query params. Name-bearing
 * filters (field/group/addedBy) go as arrays — repeated query params — because the
 * values themselves can contain commas; the rest are fixed enums, safe to comma-join.
 */
const reviewFilterParams = (f: ReviewFilters) => ({
  domain: f.domain?.join(',') || undefined,
  field: f.field?.length ? f.field : undefined,
  groupName: f.groupName?.length ? f.groupName : undefined,
  addedBy: f.addedBy?.length ? f.addedBy : undefined,
  golden: f.golden?.join(',') || undefined,
  linkedinVerified: f.linkedinVerified?.join(',') || undefined,
  resumeVerification: f.resumeVerification?.join(',') || undefined,
  webAgentVerification: f.webAgentVerification?.join(',') || undefined,
  agentRecommendation: f.agentRecommendation?.join(',') || undefined,
  reviewTags: f.reviewTags?.join(',') || undefined,
  idvSubWorkVerified: f.idvSubWorkVerified?.join(',') || undefined,
  idvSubEduVerified: f.idvSubEduVerified?.join(',') || undefined,
  idvSubIdVerified: f.idvSubIdVerified?.join(',') || undefined,
  idvSubLinkedinFound: f.idvSubLinkedinFound?.join(',') || undefined,
  idvSubAccountUnder3y: f.idvSubAccountUnder3y?.join(',') || undefined,
  idvSubAccountOver3y: f.idvSubAccountOver3y?.join(',') || undefined,
  pscSubEducation: f.pscSubEducation?.join(',') || undefined,
  pscSubWork: f.pscSubWork?.join(',') || undefined,
  pscSubGithub: f.pscSubGithub?.join(',') || undefined,
  pscSubDisqualifiers: f.pscSubDisqualifiers?.join(',') || undefined,
  pscSubAgentVerdict: f.pscSubAgentVerdict?.join(',') || undefined,
});

type SelectionMap = React.MutableRefObject<Map<string, PipelineCandidate>>;

const REVIEW_FILTER_LABELS: Record<string, string> = {
  domain: 'Domain', field: 'Field', groupName: 'Group', addedBy: 'Added by', golden: 'Golden Talent',
  linkedinVerified: 'ID Verification', resumeVerification: 'Internal Resume Review',
  webAgentVerification: 'Public Source Check', agentRecommendation: 'Overall recommendation', reviewTags: 'Tags',
  idvSubWorkVerified: 'Work Verified',
  idvSubEduVerified: 'Edu Verified',
  idvSubIdVerified: 'Id Verified',
  idvSubLinkedinFound: 'LinkedIn found',
  idvSubAccountUnder3y: 'LinkedIn account <3 yrs',
  idvSubAccountOver3y: 'LinkedIn account >3 yrs',
  pscSubAgentVerdict: 'Agent verdict',
  pscSubEducation: 'Education verification',
  pscSubWork: 'Work verification',
  pscSubGithub: 'GitHub/Portfolio verification',
  pscSubDisqualifiers: 'Disqualifiers',
};

const REVIEW_SORT_LABELS: Record<string, string> = {
  talentId: 'Name', domain: 'Domain', field: 'Field', isGolden: 'Golden Talent',
  linkedinVerified: 'ID Verification', resumeVerification: 'Internal Resume Review',
  webAgentVerification: 'Public Source Check', agentRecommendation: 'Overall recommendation',
  reviewTags: 'Tags', groupName: 'Group', addedBy: 'Added by', addedAt: 'Added at',
};

const toggleSelection = (profile: PipelineCandidate, setter: React.Dispatch<React.SetStateAction<Set<string>>>, map: SelectionMap) => {
  const key = ckey(profile.talentId, profile.domain);
  setter((prev) => {
    const n = new Set(prev);
    if (n.has(key)) { n.delete(key); map.current.delete(key); }
    else { n.add(key); map.current.set(key, profile); }
    return n;
  });
};

const toggleAllSelection = (list: PipelineCandidate[], selected: Set<string>, setter: React.Dispatch<React.SetStateAction<Set<string>>>, map: SelectionMap) => {
  const allPageSelected = list.length > 0 && list.every((p) => selected.has(ckey(p.talentId, p.domain)));
  setter((prev) => {
    const next = new Set(prev);
    for (const p of list) {
      const k = ckey(p.talentId, p.domain);
      if (allPageSelected) { next.delete(k); map.current.delete(k); }
      else { next.add(k); map.current.set(k, p); }
    }
    return next;
  });
};

const GOLDEN_REMOVAL_REASONS = [
  { value: 'trust', label: 'Trust concern' },
  { value: 'fraud', label: 'Fraud concern' },
  { value: 'compliance', label: 'Compliance removal' },
  { value: 'other', label: 'Other reason' },
] as const;

/**
 * Confirmation dialog for removing profiles from Golden Talent. Requires a reason;
 * shows the global-removal warning for the fixed reasons and a free-text note for "other".
 * `count` reflects only the profiles that are currently golden.
 */
function GoldenRemovalModal({
  count, onCancel, onConfirm,
}: {
  count: number;
  onCancel: () => void;
  onConfirm: (reason: string, note?: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const canSubmit = reason !== '' && (reason !== 'other' || note.trim() !== '') && !submitting;
  const confirm = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onConfirm(reason, reason === 'other' ? note.trim() : undefined);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in duration-200">
      <div className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl animate-in zoom-in-95 duration-200">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Remove from Golden Talent</h3>
            <p className="mt-1 text-sm text-gray-500">Provide a reason for removing {count} profile{count !== 1 ? 's' : ''}.</p>
          </div>
          <button type="button" onClick={onCancel} aria-label="Close removal dialog" className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 p-6">
          <div>
            <label htmlFor="golden-removal-reason" className="mb-1.5 block text-sm font-semibold text-gray-800">Reason</label>
            <select
              id="golden-removal-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            >
              <option value="" disabled>Select a reason</option>
              {GOLDEN_REMOVAL_REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          {reason !== '' && reason !== 'other' && (
            <div className="flex gap-3 rounded-lg border border-red-100 bg-red-50 p-3 text-sm text-red-800">
              <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <p>This is a global removal. The selected profiles will immediately become unavailable to every account, including through existing batches.</p>
            </div>
          )}
          {reason === 'other' && (
            <div>
              <label htmlFor="golden-removal-note" className="mb-1.5 block text-sm font-semibold text-gray-800">Other reason</label>
              <textarea
                id="golden-removal-note"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Explain why these profiles are being removed…"
                className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <button type="button" onClick={onCancel} disabled={submitting} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <button type="button" onClick={confirm} disabled={!canSubmit} className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}Remove
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Admin star toggle for a candidate's golden-dataset membership. Self-contained:
 * optimistic local state, reverts on error. Row re-fetch re-seeds from `initial`.
 * Removing (golden → not) opens a confirmation dialog and captures a reason.
 */
function GoldenStarButton({ talentId, initial, disabled = false, onGoldenChange }: { talentId: string; initial: boolean; disabled?: boolean; onGoldenChange?: (golden: boolean) => void }) {
  const [golden, setGolden] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  useEffect(() => { setGolden(initial); }, [initial]);

  const add = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (saving) return;
    setGolden(true);
    setSaving(true);
    try {
      await setCandidateGolden(talentId, true);
      onGoldenChange?.(true);
    } catch {
      setGolden(false);
    } finally {
      setSaving(false);
    }
  };

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (saving) return;
    if (golden) { setConfirming(true); return; }
    void add(e);
  };

  const confirmRemove = async (reason: string, note?: string) => {
    setSaving(true);
    try {
      await setCandidateGolden(talentId, false, { reason, note });
      setGolden(false);
      setConfirming(false);
      onGoldenChange?.(false);
      toast.success('1 profile removed from Golden Talent');
    } catch {
      // leave dialog open on failure
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        disabled={saving || disabled}
        aria-label={golden ? 'Remove from Golden Talent' : 'Add to Golden Talent'}
        title={disabled ? 'Clear selection to edit Golden Talent' : golden ? 'Golden Talent' : 'Not Golden Talent'}
        className={`inline-flex h-7 w-7 items-center justify-center rounded-md border transition-colors disabled:opacity-50 ${golden ? 'border-amber-200 bg-amber-50 text-amber-500 hover:bg-amber-100' : 'border-gray-200 bg-white text-gray-300 hover:border-amber-200 hover:text-amber-400'}`}
      >
        <Star className={`h-4 w-4 ${golden ? 'fill-current' : ''}`} />
      </button>
      {confirming && ReactDOM.createPortal(
        <div onClick={(e) => e.stopPropagation()}>
          <GoldenRemovalModal count={1} onCancel={() => setConfirming(false)} onConfirm={confirmRemove} />
        </div>,
        document.body,
      )}
    </>
  );
}

/**
 * Bulk golden-dataset control for the selection toast — a "Golden Talent" dropdown
 * with Add / Remove. One request sets is_golden for the whole selection + reconciles.
 */
function GoldenBulkMenu({ candidates, onDone }: { candidates: PipelineCandidate[]; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Removal targets only the currently-golden profiles in the selection, not everything selected.
  const goldenIds = candidates.filter((c) => c.isGolden).map((c) => c.talentId);

  const add = async () => {
    const ids = candidates.map((c) => c.talentId);
    if (loading || ids.length === 0) return;
    setLoading(true);
    try {
      await setCandidatesGoldenBulk(ids, true);
      setOpen(false);
      onDone();
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const openRemove = () => {
    if (goldenIds.length === 0) { toast.info('No Golden Talent profiles in the selection'); setOpen(false); return; }
    setOpen(false);
    setConfirming(true);
  };

  const confirmRemove = async (reason: string, note?: string) => {
    setLoading(true);
    try {
      await setCandidatesGoldenBulk(goldenIds, false, { reason, note });
      setConfirming(false);
      onDone();
      toast.success(`${goldenIds.length} profile${goldenIds.length !== 1 ? 's' : ''} removed from Golden Talent`);
    } catch { /* leave dialog open on failure */ }
    finally { setLoading(false); }
  };

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={loading}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex whitespace-nowrap items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-100 disabled:opacity-50"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />}
        Golden Talent
        <ChevronDown className="h-4 w-4" />
      </button>
      {open && (
        <div role="menu" className="absolute bottom-full left-0 z-30 mb-2 w-44 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-xl">
          <button type="button" role="menuitem" onClick={add} disabled={loading} className="flex w-full items-center gap-2 px-3 py-2 text-left font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400">
            <Plus className="h-4 w-4" />Add
          </button>
          <button type="button" role="menuitem" onClick={openRemove} disabled={loading} className="flex w-full items-center gap-2 px-3 py-2 text-left font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400">
            <X className="h-4 w-4" />Remove
          </button>
        </div>
      )}
      {confirming && ReactDOM.createPortal(
        <GoldenRemovalModal count={goldenIds.length} onCancel={() => setConfirming(false)} onConfirm={confirmRemove} />,
        document.body,
      )}
    </div>
  );
}

/** Actor name with avatar (— when unknown). Its timestamp lives in a sibling column. */
// addedAt = latest ingestion run's start; re-ingesting an approved candidate bumps it past
// reviewedAt (approval). Only re-ingestion produces added-after-approved, so that gap flags it.
function isReingested(addedAt?: string | null, reviewedAt?: string | null): boolean {
  if (!addedAt || !reviewedAt) return false;
  const a = new Date(addedAt).getTime();
  const r = new Date(reviewedAt).getTime();
  return Number.isFinite(a) && Number.isFinite(r) && a > r;
}

// Portal tooltip: table lives in overflow-x-auto, so an absolute child would clip.
// Fixed-position via portal, shows instantly on hover.
function InfoTooltip({ text }: { text: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({ x: r.left + r.width / 2, y: r.top });
  };
  return (
    <span ref={ref} onMouseEnter={show} onMouseLeave={() => setPos(null)} className="inline-flex shrink-0 cursor-help text-gray-400 dark:text-gray-500">
      <Info className="w-3.5 h-3.5" />
      {pos && ReactDOM.createPortal(
        <span
          style={{ left: pos.x, top: pos.y - 8, transform: 'translate(-50%, -100%)' }}
          className="pointer-events-none fixed z-[9999] max-w-[220px] rounded-md bg-gray-800 px-2 py-1 text-xs font-normal text-gray-100 shadow-lg dark:bg-gray-700"
        >
          {text}
        </span>,
        document.body,
      )}
    </span>
  );
}

function ActionedByCell({ name }: { name: string | null }) {
  if (!name) return <span className="text-gray-400 text-xs">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">{getInitials(name)}</span>
      <span className="text-sm text-gray-700 dark:text-gray-300">{name}</span>
    </span>
  );
}

/**
 * The verification-heavy candidate table. Shared by In Review and Rejected — both
 * show the same columns and filters; only the row data and the bulk actions
 * (rendered by the parent) differ.
 */
function ReviewCandidatesTable({
  profiles, loading, total, totalPages, allSelected, selectAllLoading,
  selected, setSelected, selectedMap, sort, onSort, filters, onFilter, filterOpts,
  onSelectAllMatching, onClearSelection, onOpenProfile, containerRef, reviewedByLabel,
}: {
  profiles: PipelineCandidate[];
  loading: boolean;
  total: number;
  totalPages: number;
  allSelected: boolean;
  selectAllLoading: boolean;
  selected: Set<string>;
  setSelected: React.Dispatch<React.SetStateAction<Set<string>>>;
  selectedMap: SelectionMap;
  sort: { col: string; dir: 'asc' | 'desc' } | null;
  onSort: (col: string) => void;
  filters: ReviewFilters;
  onFilter: (key: keyof ReviewFilters, value: string[]) => void;
  filterOpts: ReviewFilterOpts;
  onSelectAllMatching: () => void;
  onClearSelection: () => void;
  onOpenProfile: (p: PipelineCandidate, tab: DetailTab) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** When set (e.g. "Rejected by"), renders the reviewer by+at columns. Omit for In Review. */
  reviewedByLabel?: string;
}) {
  const reviewedAtLabel = reviewedByLabel?.replace(/by$/i, 'at');
  const colSpan = 13 + (reviewedByLabel ? 2 : 0);
  return (
    <div ref={containerRef} className={`relative ${loading && profiles.length === 0 ? 'min-h-[220px]' : ''}`}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-600 dark:text-gray-400 min-w-[1100px]">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800">
            <tr>
              <th className="px-4 py-3 w-12">
                <input type="checkbox" checked={profiles.length > 0 && profiles.every((p) => selected.has(ckey(p.talentId, p.domain)))} onChange={() => toggleAllSelection(profiles, selected, setSelected, selectedMap)} className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500" />
              </th>
              <SortFilterHeader label="Name" sortKey="talentId" currentSort={sort} onSort={onSort} />
              <SortFilterHeader label="Domain" sortKey="domain" currentSort={sort} onSort={onSort} filterOptions={filterOpts.domains} filterValue={filters.domain} onFilter={(v) => onFilter('domain', v)} />
              <SortFilterHeader label="Field" sortKey="field" currentSort={sort} onSort={onSort} filterOptions={filterOpts.fields} filterValue={filters.field} onFilter={(v) => onFilter('field', v)} />
              <SortFilterHeader label="Internal Resume Review" sortKey="resumeVerification" currentSort={sort} onSort={onSort} filterOptions={['YES', 'NO']} filterValue={filters.resumeVerification} onFilter={(v) => onFilter('resumeVerification', v)} className="text-center" />
              <SortFilterHeader
                label="Public Source Check"
                sortKey="webAgentVerification"
                currentSort={sort}
                onSort={onSort}
                className="text-center"
                customFilter={(
                  <NestedFilterDropdown
                    groups={PUBLIC_SOURCE_CHECK_GROUPS}
                    filterState={{
                      webAgentVerification: filters.webAgentVerification,
                      pscSubAgentVerdict: filters.pscSubAgentVerdict,
                      pscSubDisqualifiers: filters.pscSubDisqualifiers,
                      pscSubEducation: filters.pscSubEducation,
                      pscSubWork: filters.pscSubWork,
                      pscSubGithub: filters.pscSubGithub,
                    }}
                    onChange={(key, value) => onFilter(key as keyof ReviewFilters, value)}
                  />
                )}
              />
              <SortFilterHeader
                label="ID Verification"
                sortKey="linkedinVerified"
                currentSort={sort}
                onSort={onSort}
                className="text-center"
                customFilter={(
                  <NestedFilterDropdown
                    groups={ID_VERIFICATION_GROUPS}
                    filterState={{
                      linkedinVerified: filters.linkedinVerified,
                      idvSubWorkVerified: filters.idvSubWorkVerified,
                      idvSubEduVerified: filters.idvSubEduVerified,
                      idvSubIdVerified: filters.idvSubIdVerified,
                      idvSubLinkedinFound: filters.idvSubLinkedinFound,
                      idvSubAccountUnder3y: filters.idvSubAccountUnder3y,
                      idvSubAccountOver3y: filters.idvSubAccountOver3y,
                    }}
                    onChange={(key, value) => onFilter(key as keyof ReviewFilters, value)}
                  />
                )}
              />
              <SortFilterHeader label="Overall recommendation" sortKey="agentRecommendation" currentSort={sort} onSort={onSort} filterOptions={['YES', 'NO']} filterValue={filters.agentRecommendation} onFilter={(v) => onFilter('agentRecommendation', v)} />
              <SortFilterHeader label="Tags" sortKey="reviewTags" currentSort={sort} onSort={onSort} filterOptions={filterOpts.reviewTags} filterValue={filters.reviewTags} onFilter={(v) => onFilter('reviewTags', v)} />
              <SortFilterHeader label="Group" sortKey="groupName" currentSort={sort} onSort={onSort} filterOptions={filterOpts.groups} filterValue={filters.groupName} onFilter={(v) => onFilter('groupName', v)} />
              <SortFilterHeader label="Added by" sortKey="addedBy" currentSort={sort} onSort={onSort} filterOptions={filterOpts.addedBys} filterValue={filters.addedBy} onFilter={(v) => onFilter('addedBy', v)} />
              <SortFilterHeader label="Added at" sortKey="addedAt" currentSort={sort} onSort={onSort} />
              {reviewedByLabel && <th scope="col" className="px-4 py-3 whitespace-nowrap">{reviewedByLabel}</th>}
              {reviewedByLabel && <th scope="col" className="px-4 py-3 whitespace-nowrap">{reviewedAtLabel}</th>}
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className={`divide-y divide-gray-100 dark:divide-gray-800 transition-opacity ${loading ? 'opacity-40 pointer-events-none' : ''}`}>
            {!loading && profiles.length === 0 && (
              <tr>
                <td colSpan={colSpan} className="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                  No candidates match the current filters.
                </td>
              </tr>
            )}
            {(() => {
              const allPageChecked = profiles.length > 0 && profiles.every((p) => selected.has(ckey(p.talentId, p.domain)));
              if (!allPageChecked || totalPages <= 1) return null;
              return (
                <tr>
                  <td colSpan={colSpan} className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-sm text-center border-b border-blue-100 dark:border-blue-800">
                    {allSelected ? (
                      <>
                        <span className="text-gray-700 dark:text-gray-300">All <strong>{total}</strong> candidates selected.</span>
                        {' '}
                        <button onClick={onClearSelection} className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800">Clear selection</button>
                      </>
                    ) : (
                      <>
                        <span className="text-gray-700 dark:text-gray-300">All {profiles.length} on this page selected.</span>
                        {' '}
                        {selectAllLoading ? (
                          <Loader2 className="w-3 h-3 inline animate-spin text-blue-500" />
                        ) : (
                          <button onClick={onSelectAllMatching} className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800">
                            Select all {total} candidates matching filters
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })()}
            {profiles.map((p) => {
              const key = ckey(p.talentId, p.domain);
              return (
                <tr key={key} onClick={() => onOpenProfile(p, 'fraud-check')} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group cursor-pointer">
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(key)} onChange={() => toggleSelection(p, setSelected, selectedMap)} className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500" />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{p.name || p.talentId}</div>
                    {p.name && <div className="text-xs text-gray-500 dark:text-gray-400">{p.talentId}</div>}
                  </td>
                  <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{p.domain}</span></td>
                  <td className="px-4 py-3">{p.field ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300">{p.field}</span> : <span className="text-gray-400 text-xs">—</span>}</td>
                  <td className="px-4 py-3 text-center"><div className="flex justify-center"><VerificationIcon value={p.resumeVerification} /></div></td>
                  <td className="px-4 py-3 text-center"><PublicSourceCheckCell value={p.webAgentVerification} sub={p.webAgentVerificationSub} /></td>
                  <td className="px-4 py-3 text-center"><IdVerificationCell value={p.linkedinVerified} sub={p.linkedinVerifiedSub} /></td>
                  <td className="px-4 py-3 text-center">
                    {p.agentRecommendation ? (
                      <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium ${p.agentRecommendation === 'YES' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'}`}>{p.agentRecommendation}</span>
                    ) : <span className="text-gray-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 min-w-[160px] max-w-[280px]">
                      {p.reviewTags && p.reviewTags.length > 0 ? p.reviewTags.map((tag, i) => (
                        <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-800 truncate max-w-[120px]">{tag}</span>
                      )) : <span className="text-gray-400 text-xs">—</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {p.uploadGroup ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-800 max-w-[200px] truncate">{p.uploadGroup}</span> : <span className="text-gray-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {p.addedBy ? (
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">{getInitials(p.addedBy)}</div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">{p.addedBy}</span>
                      </div>
                    ) : <span className="text-gray-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                      {formatDate(p.addedAt)}
                      {isReingested(p.addedAt, p.reviewedAt) && (
                        <InfoTooltip text="Re-ingested after review, so this timestamp reflects the latest ingestion and is later than the review date." />
                      )}
                    </span>
                  </td>
                  {reviewedByLabel && <td className="px-4 py-3 whitespace-nowrap"><ActionedByCell name={p.reviewedBy} /></td>}
                  {reviewedByLabel && <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{p.reviewedAt ? formatDate(p.reviewedAt) : <span className="text-gray-400">—</span>}</td>}
                  <td className="px-2 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={(e) => { e.stopPropagation(); onOpenProfile(p, 'view'); }} className="p-1.5 text-gray-400 group-hover:text-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors" title="Quick View">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); onOpenProfile(p, 'edit'); }} className="p-1.5 text-gray-400 group-hover:text-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors" title="Edit Profile">
                        <Pencil className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {loading && <TableLoadingOverlay containerRef={containerRef} />}
    </div>
  );
}

export function CandidatesSection({ initialGroupFilter, initialTab }: { initialGroupFilter?: string; initialTab?: Tab }) {
  const [activeTab, setActiveTab] = useState<Tab>(initialTab ?? 'candidates');

  // In Review state
  const [irProfiles, setIrProfiles] = useState<PipelineCandidate[]>([]);
  const [irLoading, setIrLoading] = useState(false);
  const [irTotal, setIrTotal] = useState(0);
  const [irPage, setIrPage] = useState(1);
  const [irPageSize, setIrPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [irTotalPages, setIrTotalPages] = useState(1);
  const [irSearch, setIrSearch] = useState('');
  const [irSelected, setIrSelected] = useState<Set<string>>(new Set());
  const [irSort, setIrSort] = useState<{ col: string; dir: 'asc' | 'desc' } | null>(null);
  const [irFilters, setIrFilters] = useState<ReviewFilters>({});
  const [irFilterOpts, setIrFilterOpts] = useState<ReviewFilterOpts>({ domains: [], fields: [], groups: [], addedBys: [], reviewTags: [] });
  const [irSummaryCounts, setIrSummaryCounts] = useState<{ all: number; passed: number; needsReview: number }>({ all: 0, passed: 0, needsReview: 0 });
  const [irStatusFilter, setIrStatusFilter] = useState<'all' | 'passed' | 'needsReview'>('all');
  const [irAllSelected, setIrAllSelected] = useState(false);
  const [irSelectAllLoading, setIrSelectAllLoading] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<PipelineCandidate | null>(null);
  const [selectedTab, setSelectedTab] = useState<DetailTab>('view');
  const [actionLoading, setActionLoading] = useState(false);

  // Candidates (accepted) state
  const [cProfiles, setCProfiles] = useState<PipelineCandidate[]>([]);
  const [cLoading, setCLoading] = useState(false);
  const [cTotal, setCTotal] = useState(0);
  const [cPage, setCPage] = useState(1);
  const [cPageSize, setCPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [cTotalPages, setCTotalPages] = useState(1);
  const [cSearch, setCSearch] = useState('');
  const [cSelected, setCSelected] = useState<Set<string>>(new Set());
  const [cSort, setCSort] = useState<{ col: string; dir: 'asc' | 'desc' } | null>(null);
  const [cFilters, setCFilters] = useState<{ domain?: string[]; field?: string[]; groupName?: string[]; addedBy?: string[]; batch?: string[]; golden?: string[] }>({});
  const [adminFilters, setAdminFilters] = useState<SearchFilters>({ domain: 'SWE' });
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // The advanced filters actually APPLIED (last "Apply"), mirrored for reactive chips.
  // Distinct from `adminFilters` (the drawer draft, which changes before Apply).
  const [appliedAdminFilters, setAppliedAdminFilters] = useState<SearchFilters>({});
  const advancedChips = Object.entries(appliedAdminFilters)
    .filter(([k, v]) => k !== 'domain' && ADV_LABELS[k] && advIsActive(v))
    .map(([k, v]) => ({ key: k, label: ADV_LABELS[k], text: advValueText(k, v) }));
  const advancedCount = advancedChips.length;
  // Applied advanced params, mapped once and shared by ALL three tab fetches
  // (advanced search is global, like the search box). {} = none applied.
  const adminParamsRef = useRef<Record<string, unknown>>({});
  const [cFilterOpts, setCFilterOpts] = useState<{ domains: string[]; fields: string[]; groups: string[]; addedBys: string[]; batches: string[] }>({ domains: [], fields: [], groups: [], addedBys: [], batches: [] });
  const [cAllSelected, setCAllSelected] = useState(false);
  const [cSelectAllLoading, setCSelectAllLoading] = useState(false);

  // Rejected state — same shape/columns as In Review, minus the pass/needs-review cards
  const [rProfiles, setRProfiles] = useState<PipelineCandidate[]>([]);
  const [rLoading, setRLoading] = useState(false);
  const [rTotal, setRTotal] = useState(0);
  const [rPage, setRPage] = useState(1);
  const [rPageSize, setRPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [rTotalPages, setRTotalPages] = useState(1);
  const [rSearch, setRSearch] = useState('');
  // Single global search box above the tabs; drives all three tabs at once. Paste
  // comma-separated talent IDs (up to 1000, exact match) or type a name/single id.
  const [globalSearch, setGlobalSearch] = useState('');
  const [rSelected, setRSelected] = useState<Set<string>>(new Set());
  const [rSort, setRSort] = useState<{ col: string; dir: 'asc' | 'desc' } | null>(null);
  const [rFilters, setRFilters] = useState<ReviewFilters>({});
  const [rFilterOpts, setRFilterOpts] = useState<ReviewFilterOpts>({ domains: [], fields: [], groups: [], addedBys: [], reviewTags: [] });
  const [rAllSelected, setRAllSelected] = useState(false);
  const [rSelectAllLoading, setRSelectAllLoading] = useState(false);

  // Batch modal
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchTargetIds, setBatchTargetIds] = useState<number[]>([]);

  // Bulk edit modal
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditTab, setBulkEditTab] = useState<Tab>('in-review');

  // Track selected profile data across pages
  const irSelectedMap = useRef<Map<string, PipelineCandidate>>(new Map());
  const cSelectedMap = useRef<Map<string, PipelineCandidate>>(new Map());
  const rSelectedMap = useRef<Map<string, PipelineCandidate>>(new Map());

  const irStateRef = useRef({ filters: irFilters, page: irPage, pageSize: irPageSize, search: irSearch, sort: irSort });
  irStateRef.current = { filters: irFilters, page: irPage, pageSize: irPageSize, search: irSearch, sort: irSort };

  const cStateRef = useRef({ filters: cFilters, page: cPage, pageSize: cPageSize, search: cSearch, sort: cSort, adminFilters: {} as Record<string, unknown> });
  // Merge (not overwrite) — preserves adminFilters, which is set imperatively by the Apply button, not by a state var mirrored here.
  cStateRef.current = { ...cStateRef.current, filters: cFilters, page: cPage, pageSize: cPageSize, search: cSearch, sort: cSort };

  const rStateRef = useRef({ filters: rFilters, page: rPage, pageSize: rPageSize, search: rSearch, sort: rSort });
  rStateRef.current = { filters: rFilters, page: rPage, pageSize: rPageSize, search: rSearch, sort: rSort };

  const irReqId = useRef(0);
  const cReqId = useRef(0);
  const rReqId = useRef(0);
  const irTableRef = useRef<HTMLDivElement>(null);
  const cTableRef = useRef<HTMLDivElement>(null);
  const rTableRef = useRef<HTMLDivElement>(null);

  const fetchIR = useCallback(async (opts?: { page?: number; pageSize?: number; search?: string; sort?: { col: string; dir: 'asc' | 'desc' } | null; filters?: typeof irFilters }) => {
    const cur = irStateRef.current;
    const pg = opts?.page ?? cur.page;
    const ps = opts?.pageSize ?? cur.pageSize;
    const search = opts?.search ?? cur.search;
    const sortVal = opts?.sort !== undefined ? opts.sort : cur.sort;
    const f = opts?.filters ?? cur.filters;
    const { domain: afDomain, ...af } = (adminParamsRef.current ?? {}) as Record<string, unknown> & { domain?: string };
    const irDomain = f.domain?.join(',') || afDomain || undefined;
    const reqId = ++irReqId.current;

    const cacheKey = `admin-ir:${JSON.stringify({ pg, ps, search: search || '', sort: sortVal, f, af, domain: irDomain })}`;
    const cached = getCached<{ candidates: PipelineCandidate[]; total: number; totalPages: number; filterOptions?: any; summaryCounts?: any }>(cacheKey);
    if (cached) {
      setIrProfiles(cached.candidates);
      setIrTotal(cached.total);
      setIrTotalPages(cached.totalPages);
      if (cached.filterOptions) setIrFilterOpts({ ...cached.filterOptions, fields: cached.filterOptions.fields ?? [], reviewTags: cached.filterOptions.reviewTags ?? [] });
      if (cached.summaryCounts) setIrSummaryCounts(cached.summaryCounts);
    } else {
      setIrLoading(true);
    }

    try {
      const res = await listPipelineCandidates({
        status: 'in_review', page: pg, pageSize: ps,
        search: search || undefined,
        sortCol: sortVal?.col, sortDir: sortVal?.dir,
        ...reviewFilterParams(f),
        ...af,
        domain: irDomain,
      });
      if (reqId !== irReqId.current) return;
      setCache(cacheKey, res);
      setIrProfiles(res.candidates);
      setIrTotal(res.total);
      setIrTotalPages(res.totalPages);
      if (res.filterOptions) setIrFilterOpts({ ...res.filterOptions, fields: res.filterOptions.fields ?? [], reviewTags: res.filterOptions.reviewTags ?? [] });
      if (res.summaryCounts) setIrSummaryCounts(res.summaryCounts);
    } catch { /* silent */ }
    finally { if (reqId === irReqId.current) setIrLoading(false); }
  }, []);

  const fetchR = useCallback(async (opts?: { page?: number; pageSize?: number; search?: string; sort?: { col: string; dir: 'asc' | 'desc' } | null; filters?: ReviewFilters }) => {
    const cur = rStateRef.current;
    const pg = opts?.page ?? cur.page;
    const ps = opts?.pageSize ?? cur.pageSize;
    const search = opts?.search ?? cur.search;
    const sortVal = opts?.sort !== undefined ? opts.sort : cur.sort;
    const f = opts?.filters ?? cur.filters;
    const { domain: afDomain, ...af } = (adminParamsRef.current ?? {}) as Record<string, unknown> & { domain?: string };
    const rDomain = f.domain?.join(',') || afDomain || undefined;
    const reqId = ++rReqId.current;

    const cacheKey = `admin-r:${JSON.stringify({ pg, ps, search: search || '', sort: sortVal, f, af, domain: rDomain })}`;
    const cached = getCached<{ candidates: PipelineCandidate[]; total: number; totalPages: number; filterOptions?: any }>(cacheKey);
    if (cached) {
      setRProfiles(cached.candidates);
      setRTotal(cached.total);
      setRTotalPages(cached.totalPages);
      if (cached.filterOptions) setRFilterOpts({ ...cached.filterOptions, fields: cached.filterOptions.fields ?? [], reviewTags: cached.filterOptions.reviewTags ?? [] });
    } else {
      setRLoading(true);
    }

    try {
      const res = await listPipelineCandidates({
        status: 'rejected', page: pg, pageSize: ps,
        search: search || undefined,
        sortCol: sortVal?.col, sortDir: sortVal?.dir,
        ...reviewFilterParams(f),
        ...af,
        domain: rDomain,
      });
      if (reqId !== rReqId.current) return;
      setCache(cacheKey, res);
      setRProfiles(res.candidates);
      setRTotal(res.total);
      setRTotalPages(res.totalPages);
      if (res.filterOptions) setRFilterOpts({ ...res.filterOptions, fields: res.filterOptions.fields ?? [], reviewTags: res.filterOptions.reviewTags ?? [] });
    } catch { /* silent */ }
    finally { if (reqId === rReqId.current) setRLoading(false); }
  }, []);

  const adminFiltersToParams = (f: SearchFilters) => ({
    query: f.searchQuery?.trim() || undefined,
    // NOTE: do NOT emit domain here. The panel has no domain picker (always 'SWE'),
    // and under bypassAccess the ES query is cross-domain anyway — so pinning domain
    // would wrongly add a MySQL ptr.domain='swe' filter, hiding STEM/Finance/etc.
    // MySQL domain scoping comes solely from the table's Domain column filter.
    companies: f.companies?.length ? f.companies : undefined,
    currentCompanies: f.currentCompanies?.length ? f.currentCompanies : undefined,
    pastCompanies: f.pastCompanies?.length ? f.pastCompanies : undefined,
    skills: f.skills?.some((s) => s.name?.trim()) ? f.skills.filter((s) => s.name?.trim()).map((s) => ({ name: s.name })) : undefined,
    education: f.education?.length ? f.education : undefined,
    schools: f.school ? [f.school] : undefined,
    countries: f.countries?.length ? f.countries : undefined,
    usState: f.usState?.length ? f.usState : undefined,
    yearOfExperience: f.yearOfExperience,
    hourlyRate: f.hourlyRate,
    certificates: f.certificates || undefined,
    researchPapersCount: f.researchPapersCount,
    naturalLanguages: f.naturalLanguages?.length ? f.naturalLanguages : undefined,
    programmingLanguages: f.programmingLanguages?.length ? f.programmingLanguages : undefined,
    typeOfWork: f.typeOfWork || undefined,
    esField: f.field || undefined,
    domainFilter: f.domainFilter || undefined,
    hasGithubData: f.hasGithubData,
    titles: f.titles?.length ? f.titles : undefined,
    countriesExclude: f.countriesExclude?.length ? f.countriesExclude : undefined,
    companiesExclude: f.companiesExclude?.length ? f.companiesExclude : undefined,
    skillsExclude: f.skillsExclude?.length ? f.skillsExclude : undefined,
  });

  const fetchC = useCallback(async (opts?: { page?: number; pageSize?: number; search?: string; sort?: { col: string; dir: 'asc' | 'desc' } | null; filters?: typeof cFilters; adminFilters?: ReturnType<typeof adminFiltersToParams> }) => {
    const cur = cStateRef.current;
    const pg = opts?.page ?? cur.page;
    const ps = opts?.pageSize ?? cur.pageSize;
    const search = opts?.search ?? cur.search;
    const sortVal = opts?.sort !== undefined ? opts.sort : cur.sort;
    const f = opts?.filters ?? cur.filters;
    // Table's own Domain column filter always wins over a stale Apply-time admin-panel
    // domain — drop `domain` out of the spread so it can never clobber `f.domain` below.
    const { domain: afDomain, ...af } = (opts?.adminFilters ?? adminParamsRef.current ?? {}) as Record<string, unknown> & { domain?: string };
    const resolvedDomain = f.domain?.join(',') || afDomain || undefined;
    const reqId = ++cReqId.current;

    const cacheKey = `admin-c:${JSON.stringify({ pg, ps, search: search || '', sort: sortVal, f, af, domain: resolvedDomain })}`;
    const cached = getCached<{ candidates: PipelineCandidate[]; total: number; totalPages: number; filterOptions?: any }>(cacheKey);
    if (cached) {
      setCProfiles(cached.candidates);
      setCTotal(cached.total);
      setCTotalPages(cached.totalPages);
      if (cached.filterOptions) setCFilterOpts({ domains: cached.filterOptions.domains, fields: cached.filterOptions.fields ?? [], groups: cached.filterOptions.groups, addedBys: cached.filterOptions.addedBys, batches: cached.filterOptions.batches ?? [] });
    } else {
      setCLoading(true);
    }

    try {
      const res = await listPipelineCandidates({
        status: 'accepted', page: pg, pageSize: ps,
        search: search || undefined,
        sortCol: sortVal?.col, sortDir: sortVal?.dir,
        field: f.field?.length ? f.field : undefined,
        groupName: f.groupName?.length ? f.groupName : undefined,
        addedBy: f.addedBy?.length ? f.addedBy : undefined,
        batch: f.batch?.length ? f.batch : undefined,
        golden: f.golden?.join(',') || undefined,
        ...af,
        domain: resolvedDomain,
      });
      if (reqId !== cReqId.current) return;
      setCache(cacheKey, res);
      setCProfiles(res.candidates);
      setCTotal(res.total);
      setCTotalPages(res.totalPages);
      if (res.filterOptions) setCFilterOpts({ domains: res.filterOptions.domains, fields: res.filterOptions.fields ?? [], groups: res.filterOptions.groups, addedBys: res.filterOptions.addedBys, batches: res.filterOptions.batches ?? [] });
      // Hydrate the panel from what the NLP/ES resolver actually applied (echo-back),
      // mirroring SearchPage's appliedFilters hydration — panel-visible fields only.
      const applied = (res as unknown as { appliedFilters?: AppliedFilters }).appliedFilters;
      if (applied) {
        setAdminFilters((prev) => ({
          ...prev,
          companies: applied.companies ?? prev.companies,
          currentCompanies: applied.currentCompanies ?? prev.currentCompanies,
          pastCompanies: applied.pastCompanies ?? prev.pastCompanies,
          skills: applied.skills?.some((s: any) => s?.name?.trim()) ? applied.skills.filter((s: any) => s?.name?.trim()) : prev.skills,
          education: (applied.education as SearchFilters['education']) ?? prev.education,
          countries: applied.countries ?? prev.countries,
          titles: applied.titles ?? prev.titles,
          certificates: applied.certificates ?? prev.certificates,
          researchPapersCount: applied.researchPapersCount ?? prev.researchPapersCount,
          naturalLanguages: applied.naturalLanguages ?? prev.naturalLanguages,
          programmingLanguages: applied.programmingLanguages ?? prev.programmingLanguages,
          typeOfWork: applied.typeOfWork ?? prev.typeOfWork,
          hasGithubData: applied.hasGithubData ?? prev.hasGithubData,
          school: applied.schools?.[0] ?? prev.school,
          field: applied.field ?? prev.field,
        }));
      }
    } catch { /* silent */ }
    finally { if (reqId === cReqId.current) setCLoading(false); }
  }, []);

  // Admin console doesn't otherwise load domain config (only SearchPage does) — the
  // AdminFilterSection panel needs it for skills/expertise/field options per domain.
  const [adminConfigReady, setAdminConfigReady] = useState(false);
  useEffect(() => { fetchAppConfig().then(() => setAdminConfigReady(true)); }, []);

  // Fetch on mount — one call per tab, guarded against StrictMode double-invoke
  const mountedRef = useRef(false);
  const irFetchedRef = useRef(false);
  const cFetchedRef = useRef(false);
  const rFetchedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    if (initialGroupFilter) {
      const f = { groupName: [initialGroupFilter] };
      setIrFilters(f);
      setCFilters(f);
      setRFilters(f);
      irStateRef.current = { ...irStateRef.current, filters: f };
      cStateRef.current = { ...cStateRef.current, filters: f };
      rStateRef.current = { ...rStateRef.current, filters: f };
      fetchIR({ filters: f });
      fetchC({ filters: f });
      fetchR({ filters: f });
    } else {
      fetchIR();
      fetchC();
      fetchR();
    }
    irFetchedRef.current = true;
    cFetchedRef.current = true;
    rFetchedRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-switch to the tab that has results after initial load (skipped when a tab was explicitly requested)
  const autoSwitchedRef = useRef(!!initialTab);
  useEffect(() => {
    if (!mountedRef.current || autoSwitchedRef.current || irLoading || cLoading) return;
    autoSwitchedRef.current = true;
    if (irTotal === 0 && cTotal > 0) setActiveTab('candidates');
    else if (irTotal > 0) setActiveTab('in-review');
  }, [irLoading, cLoading, irTotal, cTotal]);

  // Clear the other tabs' selections when switching tabs
  useEffect(() => {
    if (!mountedRef.current) return;
    if (activeTab !== 'in-review') clearIrSelection();
    if (activeTab !== 'candidates') clearCSelection();
    if (activeTab !== 'rejected') clearRSelection();
  }, [activeTab]);

  // Re-fetch only the active tab when switching, skip if already fetched on mount
  useEffect(() => {
    if (!mountedRef.current) return;
    if (activeTab === 'in-review') {
      if (irFetchedRef.current) { irFetchedRef.current = false; return; }
      fetchIR();
    }
    if (activeTab === 'candidates') {
      if (cFetchedRef.current) { cFetchedRef.current = false; return; }
      fetchC();
    }
    if (activeTab === 'rejected') {
      if (rFetchedRef.current) { rFetchedRef.current = false; return; }
      fetchR();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const filteredIrProfiles = irProfiles;

  // Pill text: for a pasted ID list show a count (the raw blob is too wide); else the term.
  const searchIdCount = idTokens(globalSearch).length;
  const searchChipLabel = searchIdCount > 0
    ? `${searchIdCount} talent ID${searchIdCount === 1 ? '' : 's'}`
    : globalSearch;

  // With any filter/search/sort narrowing active, an empty result is a "no matches"
  // state inside the table (header + filter controls stay reachable), not the
  // "nothing here yet" empty state that replaces the whole table.
  // advancedCount is global (applies to all three tabs), so it counts as "narrowed" everywhere.
  const irNarrowed = Object.values(irFilters).some((v) => v && v.length > 0) || !!irSearch || irStatusFilter !== 'all' || advancedCount > 0;
  const cNarrowed = Object.values(cFilters).some((v) => v && v.length > 0) || !!cSearch || advancedCount > 0;
  const rNarrowed = Object.values(rFilters).some((v) => v && v.length > 0) || !!rSearch || advancedCount > 0;
  const irShowTable = irLoading || irTotal > 0 || filteredIrProfiles.length > 0 || irNarrowed;
  const cShowTable = cLoading || cTotal > 0 || cProfiles.length > 0 || cNarrowed;
  const rShowTable = rLoading || rTotal > 0 || rProfiles.length > 0 || rNarrowed;

  const toggle = toggleSelection;
  const toggleAll = toggleAllSelection;

  const clearIrSelection = () => { setIrSelected(new Set()); irSelectedMap.current.clear(); setIrAllSelected(false); };
  const clearCSelection = () => { setCSelected(new Set()); cSelectedMap.current.clear(); setCAllSelected(false); };
  const clearRSelection = () => { setRSelected(new Set()); rSelectedMap.current.clear(); setRAllSelected(false); };

  // Fan one search term out to all three tabs (page reset, refetch each). Box value +
  // over-limit guard update instantly; the three fetches are debounced so typing a name
  // fires one round of requests, not three per keystroke. A paste is a single event.
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); }, []);
  const applyGlobalSearch = (v: string) => {
    setGlobalSearch(v); // keep pasted text visible so an over-limit list can be trimmed
    // Limit is on UNIQUE ids actually searched (dupes/blanks don't count). Over the cap:
    // block AND clear rows so a stale result set can't look like a successful search.
    const n = idTokens(v).length;
    if (n > 1000) {
      toast.error(`Max 1000 talent IDs. Your list has ${n} unique — trim it and try again.`);
      // Kill any pending debounce and invalidate in-flight fetches so a stale
      // response can't refill the grids under an over-limit query that never ran.
      if (searchDebounce.current) clearTimeout(searchDebounce.current);
      // Bumping the reqIds also makes any in-flight fetch's finally-guard skip its
      // setLoading(false), so clear the spinners here or the badges spin forever.
      ++irReqId.current; ++cReqId.current; ++rReqId.current;
      setIrLoading(false); setCLoading(false); setRLoading(false);
      // Reset every count-bearing bit together — tab badges, page counts, and the
      // In-Review summary chips — else the chips keep stale numbers under a 0 total.
      setIrProfiles([]); setCProfiles([]); setRProfiles([]);
      setIrTotal(0); setCTotal(0); setRTotal(0);
      setIrTotalPages(1); setCTotalPages(1); setRTotalPages(1);
      setIrSummaryCounts({ all: 0, passed: 0, needsReview: 0 });
      return;
    }
    clearIrSelection(); clearCSelection(); clearRSelection();
    setIrSearch(v); setCSearch(v); setRSearch(v);
    setIrPage(1); setCPage(1); setRPage(1);
    irStateRef.current = { ...irStateRef.current, search: v, page: 1 };
    cStateRef.current = { ...cStateRef.current, search: v, page: 1 };
    rStateRef.current = { ...rStateRef.current, search: v, page: 1 };
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      fetchIR({ page: 1, search: v });
      fetchC({ page: 1, search: v });
      fetchR({ page: 1, search: v });
    }, 1000);
  };

  // Keep list rows + selection snapshot in sync when a single-row star toggles golden,
  // so a later multi-select reads the latest status (not a stale isGolden).
  const syncCandidateGolden = (talentId: string, domain: string, golden: boolean) => {
    setCProfiles((prev) => prev.map((p) => (p.talentId === talentId && p.domain === domain ? { ...p, isGolden: golden } : p)));
    const k = ckey(talentId, domain);
    const snap = cSelectedMap.current.get(k);
    if (snap) cSelectedMap.current.set(k, { ...snap, isGolden: golden });
  };

  const handleIrSelectAll = async () => {
    const cur = irStateRef.current;
    setIrSelectAllLoading(true);
    try {
      const res = await listPipelineCandidates({
        status: 'in_review', page: 1, pageSize: 9999,
        search: cur.search || undefined,
        sortCol: cur.sort?.col, sortDir: cur.sort?.dir,
        ...reviewFilterParams(cur.filters),
        ...adminParamsRef.current,
      });
      const newSet = new Set<string>();
      for (const p of res.candidates) {
        const k = ckey(p.talentId, p.domain);
        newSet.add(k);
        irSelectedMap.current.set(k, p);
      }
      setIrSelected(newSet);
      setIrAllSelected(true);
    } catch { /* silent */ }
    finally { setIrSelectAllLoading(false); }
  };

  const handleCSelectAll = async () => {
    const cur = cStateRef.current;
    setCSelectAllLoading(true);
    try {
      const res = await listPipelineCandidates({
        status: 'accepted', page: 1, pageSize: 9999,
        search: cur.search || undefined,
        sortCol: cur.sort?.col, sortDir: cur.sort?.dir,
        domain: cur.filters.domain?.join(',') || undefined,
        field: cur.filters.field?.length ? cur.filters.field : undefined,
        groupName: cur.filters.groupName?.length ? cur.filters.groupName : undefined,
        addedBy: cur.filters.addedBy?.length ? cur.filters.addedBy : undefined,
        batch: (cur.filters as any).batch?.length ? (cur.filters as any).batch : undefined,
        golden: cur.filters.golden?.join(',') || undefined,
        ...adminParamsRef.current,
      });
      const newSet = new Set<string>();
      for (const p of res.candidates) {
        const k = ckey(p.talentId, p.domain);
        newSet.add(k);
        cSelectedMap.current.set(k, p);
      }
      setCSelected(newSet);
      setCAllSelected(true);
    } catch { /* silent */ }
    finally { setCSelectAllLoading(false); }
  };

  const handleRSelectAll = async () => {
    const cur = rStateRef.current;
    setRSelectAllLoading(true);
    try {
      const res = await listPipelineCandidates({
        status: 'rejected', page: 1, pageSize: 9999,
        search: cur.search || undefined,
        sortCol: cur.sort?.col, sortDir: cur.sort?.dir,
        ...reviewFilterParams(cur.filters),
        ...adminParamsRef.current,
      });
      const newSet = new Set<string>();
      for (const p of res.candidates) {
        const k = ckey(p.talentId, p.domain);
        newSet.add(k);
        rSelectedMap.current.set(k, p);
      }
      setRSelected(newSet);
      setRAllSelected(true);
    } catch { /* silent */ }
    finally { setRSelectAllLoading(false); }
  };

  const handleApprove = async (candidates: PipelineCandidate[]) => {
    setActionLoading(true);
    try {
      await approveProfiles(candidates.map((c) => c.id));
      setSelectedProfile(null);
      clearIrSelection();
      clearRSelection();
      invalidateCachePrefix('admin-ir');
      invalidateCachePrefix('admin-c');
      invalidateCachePrefix('admin-r');
      fetchIR();
      fetchC();
      fetchR();
    } catch { /* silent */ }
    finally { setActionLoading(false); }
  };

  const handleReject = async (candidates: PipelineCandidate[]) => {
    setActionLoading(true);
    try {
      await rejectProfiles(candidates.map((c) => c.id));
      setSelectedProfile(null);
      clearIrSelection();
      invalidateCachePrefix('admin-ir');
      invalidateCachePrefix('admin-r');
      fetchIR();
      fetchR();
    } catch { /* silent */ }
    finally { setActionLoading(false); }
  };

  const handleMoveToReview = async (candidates: PipelineCandidate[]) => {
    setActionLoading(true);
    try {
      await moveToReview(candidates.map((c) => c.id));
      clearCSelection();
      invalidateCachePrefix('admin-ir');
      invalidateCachePrefix('admin-c');
      fetchC();
      fetchIR();
    } catch { /* silent */ }
    finally { setActionLoading(false); }
  };

  /** Rejected → In Review, so the normal review/approve path applies again. */
  const handleRestoreToReview = async (candidates: PipelineCandidate[]) => {
    setActionLoading(true);
    try {
      await moveToReview(candidates.map((c) => c.id));
      setSelectedProfile(null);
      clearRSelection();
      invalidateCachePrefix('admin-ir');
      invalidateCachePrefix('admin-r');
      fetchR();
      fetchIR();
    } catch { /* silent */ }
    finally { setActionLoading(false); }
  };

  const getSelected = (tab: Tab): PipelineCandidate[] => {
    const map = tab === 'in-review' ? irSelectedMap : tab === 'rejected' ? rSelectedMap : cSelectedMap;
    return Array.from(map.current.values());
  };

  const handleIrSort = (col: string) => {
    clearIrSelection();
    const newSort = irSort?.col === col
      ? { col, dir: (irSort.dir === 'asc' ? 'desc' : 'asc') as 'asc' | 'desc' }
      : { col, dir: 'asc' as const };
    setIrSort(newSort);
    setIrPage(1);
    irStateRef.current = { ...irStateRef.current, sort: newSort, page: 1 };
    fetchIR({ page: 1, sort: newSort });
  };

  const handleIrFilter = (key: keyof typeof irFilters, value: string[]) => {
    clearIrSelection();
    const newFilters = { ...irStateRef.current.filters, [key]: value.length > 0 ? value : undefined };
    setIrFilters(newFilters);
    setIrPage(1);
    irStateRef.current = { ...irStateRef.current, filters: newFilters, page: 1 };
    fetchIR({ page: 1, filters: newFilters });
  };

  const handleRSort = (col: string) => {
    clearRSelection();
    const newSort = rSort?.col === col
      ? { col, dir: (rSort.dir === 'asc' ? 'desc' : 'asc') as 'asc' | 'desc' }
      : { col, dir: 'asc' as const };
    setRSort(newSort);
    setRPage(1);
    rStateRef.current = { ...rStateRef.current, sort: newSort, page: 1 };
    fetchR({ page: 1, sort: newSort });
  };

  const handleRFilter = (key: keyof ReviewFilters, value: string[]) => {
    clearRSelection();
    const newFilters = { ...rStateRef.current.filters, [key]: value.length > 0 ? value : undefined };
    setRFilters(newFilters);
    setRPage(1);
    rStateRef.current = { ...rStateRef.current, filters: newFilters, page: 1 };
    fetchR({ page: 1, filters: newFilters });
  };

  const handleIrStatusFilter = (status: 'all' | 'passed' | 'needsReview') => {
    clearIrSelection();
    setIrStatusFilter(status);
    const agentRec = status === 'all' ? undefined : status === 'passed' ? ['YES'] : ['NO'];
    const newFilters = { ...irStateRef.current.filters, agentRecommendation: agentRec };
    setIrFilters(newFilters);
    setIrPage(1);
    irStateRef.current = { ...irStateRef.current, filters: newFilters, page: 1 };
    fetchIR({ page: 1, filters: newFilters });
  };

  const handleCSort = (col: string) => {
    clearCSelection();
    const newSort = cSort?.col === col
      ? { col, dir: (cSort.dir === 'asc' ? 'desc' : 'asc') as 'asc' | 'desc' }
      : { col, dir: 'asc' as const };
    setCSort(newSort);
    setCPage(1);
    cStateRef.current = { ...cStateRef.current, sort: newSort, page: 1 };
    fetchC({ page: 1, sort: newSort });
  };

  const handleCFilter = (key: keyof typeof cFilters, value: string[]) => {
    clearCSelection();
    const newFilters = { ...cStateRef.current.filters, [key]: value.length > 0 ? value : undefined };
    setCFilters(newFilters);
    setCPage(1);
    cStateRef.current = { ...cStateRef.current, filters: newFilters, page: 1 };
    fetchC({ page: 1, filters: newFilters });
  };

  // Apply an advanced-filter set: remember it (for chips), then fetch. When nothing
  // active remains, fetch WITHOUT an adminFilters opt so fetchC reads the emptied ref
  // (no domain pin, no ES intersect → MySQL-only).
  const applyAdvanced = (next: SearchFilters) => {
    clearCSelection(); clearIrSelection(); clearRSelection();
    setAppliedAdminFilters(next);
    const active = Object.entries(next).some(([k, v]) => k !== 'domain' && advIsActive(v));
    adminParamsRef.current = active ? adminFiltersToParams(next) : {};
    // Advanced search is global — reset all three tabs to page 1 and refetch so every
    // tab's count + contents reflect the applied filters (the search box works the same way).
    setCPage(1); setIrPage(1); setRPage(1);
    fetchC({ page: 1 });
    fetchIR({ page: 1 });
    fetchR({ page: 1 });
  };
  const removeAdvanced = (key: string) => {
    const next = { ...appliedAdminFilters, [key]: undefined } as SearchFilters;
    setAdminFilters((prev) => ({ ...prev, [key]: undefined }));
    applyAdvanced(next);
  };
  const clearAdvanced = () => {
    setAdminFilters({ domain: 'SWE' });
    applyAdvanced({});
  };

  const openBatchModal = () => {
    const sel = getSelected('candidates');
    setBatchTargetIds(sel.map((c) => c.id));
    setShowBatchModal(true);
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto space-y-6 transition-opacity duration-300">
      <Toaster />
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Candidates</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Manage all candidates in the system.</p>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
        {/* Global search — one box drives all three tabs */}
        <SearchBar
          value={globalSearch}
          onChange={applyGlobalSearch}
          placeholder="Search by name, or paste talent IDs separated by commas..."
          rightSlot={(
            <button
              type="button"
              onClick={() => setAdvancedOpen(true)}
              className="flex-shrink-0 inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Advanced search
              {advancedCount > 0 && (
                <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-xs">
                  {advancedCount}
                </span>
              )}
            </button>
          )}
        />
        {/* Tab Bar */}
        <div className="border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center px-4">
            {(['candidates', 'in-review', 'rejected'] as Tab[]).map((tab) => {
              const count = tab === 'candidates' ? cTotal : tab === 'in-review' ? irTotal : rTotal;
              const loading = tab === 'candidates' ? cLoading : tab === 'in-review' ? irLoading : rLoading;
              const label = tab === 'candidates' ? 'Candidates' : tab === 'in-review' ? 'In Review' : 'Rejected';
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex items-center gap-2 py-4 px-1 mr-6 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {label}
                  {(loading || count > 0) && (
                    <span className={`px-2 py-0.5 rounded-full text-xs ${activeTab === tab ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}>
                      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Advanced search — global across all three tabs (like the search box). */}
        {advancedChips.length > 0 && (
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Advanced filters:</span>
            {advancedChips.map((c) => (
              <div key={c.key} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800">
                <span className="font-medium">{c.label}:</span>
                <span className="inline-block max-w-[220px] truncate align-bottom">{c.text}</span>
                <button onClick={() => removeAdvanced(c.key)} className="p-0.5 hover:bg-indigo-100 dark:hover:bg-indigo-800 rounded text-indigo-500 hover:text-indigo-800 transition-colors">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <button onClick={clearAdvanced} className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline ml-1">Clear all</button>
          </div>
        )}
        <Sheet open={advancedOpen} onOpenChange={setAdvancedOpen}>
          <SheetContent side="right" className="w-full sm:w-[440px] sm:max-w-[440px] p-0 flex flex-col">
            <SheetHeader className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
              <SheetTitle>Advanced filters</SheetTitle>
            </SheetHeader>
            <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
              <AdminFilterSection
                filters={adminFilters}
                onFilterChange={setAdminFilters}
                fillHeight
                configReady={adminConfigReady}
              />
            </div>
            <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-800 flex items-center gap-2">
              <button
                type="button"
                onClick={() => { applyAdvanced(adminFilters); setAdvancedOpen(false); }}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
              >
                Apply &amp; close
              </button>
              <button
                type="button"
                onClick={clearAdvanced}
                className="inline-flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Clear
              </button>
            </div>
          </SheetContent>
        </Sheet>

        {/* Active filter chips (search moved to the global box above the tabs) */}
        {activeTab === 'in-review' && (
          <>
            <FilterChips
              filters={irFilters as Record<string, string[] | undefined>}
              labels={REVIEW_FILTER_LABELS}
              onRemove={(key) => handleIrFilter(key as keyof ReviewFilters, [])}
              onClearAll={() => { clearIrSelection(); setIrFilters({}); setIrSort(null); setIrStatusFilter('all'); irStateRef.current = { ...irStateRef.current, filters: {}, sort: null }; applyGlobalSearch(''); }}
              sort={irSort}
              sortLabels={REVIEW_SORT_LABELS}
              onRemoveSort={() => { clearIrSelection(); setIrSort(null); irStateRef.current = { ...irStateRef.current, sort: null }; fetchIR({ sort: null }); }}
              search={searchChipLabel}
              onRemoveSearch={() => applyGlobalSearch('')}
            />
          </>
        )}
        {activeTab === 'candidates' && (
          <>
            <FilterChips
              filters={cFilters as Record<string, string[] | undefined>}
              labels={{ domain: 'Domain', field: 'Field', groupName: 'Group', addedBy: 'Added by', batch: 'Batch', golden: 'Golden Talent' }}
              onRemove={(key) => handleCFilter(key as any, [])}
              onClearAll={() => { clearCSelection(); setCFilters({}); setCSort(null); cStateRef.current = { ...cStateRef.current, filters: {}, sort: null }; applyGlobalSearch(''); }}
              sort={cSort}
              sortLabels={{ talentId: 'Name', domain: 'Domain', field: 'Field', isGolden: 'Golden Talent', batch: 'Batches', groupName: 'Group', addedAt: 'Date Added' }}
              onRemoveSort={() => { clearCSelection(); setCSort(null); cStateRef.current = { ...cStateRef.current, sort: null }; fetchC({ sort: null }); }}
              search={searchChipLabel}
              onRemoveSearch={() => applyGlobalSearch('')}
            />
          </>
        )}
        {activeTab === 'rejected' && (
          <>
            <FilterChips
              filters={rFilters as Record<string, string[] | undefined>}
              labels={REVIEW_FILTER_LABELS}
              onRemove={(key) => handleRFilter(key as keyof ReviewFilters, [])}
              onClearAll={() => { clearRSelection(); setRFilters({}); setRSort(null); rStateRef.current = { ...rStateRef.current, filters: {}, sort: null }; applyGlobalSearch(''); }}
              sort={rSort}
              sortLabels={REVIEW_SORT_LABELS}
              onRemoveSort={() => { clearRSelection(); setRSort(null); rStateRef.current = { ...rStateRef.current, sort: null }; fetchR({ sort: null }); }}
              search={searchChipLabel}
              onRemoveSearch={() => applyGlobalSearch('')}
            />
          </>
        )}

        {/* Status filter cards */}
        {activeTab === 'in-review' && (
          <div className="grid gap-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30 p-4 sm:grid-cols-3">
            {([
              { key: 'all' as const, label: 'All', count: irSummaryCounts.all, icon: <Users className="h-4 w-4 text-gray-500" /> },
              { key: 'passed' as const, label: 'Passed', count: irSummaryCounts.passed, icon: <CircleCheck className="h-4 w-4 text-emerald-600" /> },
              { key: 'needsReview' as const, label: 'Needs review', count: irSummaryCounts.needsReview, icon: <CircleAlert className="h-4 w-4 text-amber-600" /> },
            ]).map((card) => (
              <button
                key={card.key}
                type="button"
                onClick={() => handleIrStatusFilter(card.key)}
                aria-pressed={irStatusFilter === card.key}
                className={`min-h-[82px] rounded-xl border bg-white dark:bg-gray-900 px-4 py-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${
                  irStatusFilter === card.key
                    ? 'border-blue-300 ring-2 ring-blue-100 dark:border-blue-600 dark:ring-blue-900/40'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-2">
                  {card.icon}
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">{card.label}</span>
                </div>
                <div className="mt-2 text-2xl font-semibold tracking-normal text-gray-950 dark:text-gray-100">{card.count}</div>
              </button>
            ))}
          </div>
        )}

        {/* In Review Table */}
        {activeTab === 'in-review' && (
          <>
            {!irShowTable && (
              <div className="p-6 text-center py-12">
                <Clock className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">No candidates in review</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">Add candidates via Data Ingestion to see them here.</p>
              </div>
            )}
            {irShowTable && (
              <ReviewCandidatesTable
                profiles={filteredIrProfiles}
                loading={irLoading}
                total={irTotal}
                totalPages={irTotalPages}
                allSelected={irAllSelected}
                selectAllLoading={irSelectAllLoading}
                selected={irSelected}
                setSelected={setIrSelected}
                selectedMap={irSelectedMap}
                sort={irSort}
                onSort={handleIrSort}
                filters={irFilters}
                onFilter={(key, value) => handleIrFilter(key as keyof typeof irFilters, value)}
                filterOpts={irFilterOpts}
                onSelectAllMatching={handleIrSelectAll}
                onClearSelection={clearIrSelection}
                onOpenProfile={(p, tab) => { setSelectedProfile(p); setSelectedTab(tab); }}
                containerRef={irTableRef}
              />
            )}
            <Pagination page={irPage} totalPages={irTotalPages} total={irTotal} pageSize={irPageSize} onPageChange={(p) => { setIrPage(p); irStateRef.current = { ...irStateRef.current, page: p }; fetchIR({ page: p }); }} onPageSizeChange={(size) => { setIrPageSize(size); setIrPage(1); irStateRef.current = { ...irStateRef.current, pageSize: size, page: 1 }; fetchIR({ page: 1, pageSize: size }); }} />
          </>
        )}

        {/* Candidates (Accepted) Table */}
        {activeTab === 'candidates' && (
          <>
            {!cShowTable && (
              <div className="p-6 text-center py-12">
                <Users className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">Candidate Pool</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">Approved candidates will appear here.</p>
              </div>
            )}
            {cShowTable && (
              <div ref={cTableRef} className={`relative ${cLoading && cProfiles.length === 0 ? 'min-h-[220px]' : ''}`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-600 dark:text-gray-400 min-w-[800px]">
                  <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800">
                    <tr>
                      <th className="px-4 py-3 w-12">
                        <input type="checkbox" checked={cProfiles.length > 0 && cProfiles.every((p) => cSelected.has(ckey(p.talentId, p.domain)))} onChange={() => toggleAll(cProfiles, cSelected, setCSelected, cSelectedMap)} className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500" />
                      </th>
                      <SortFilterHeader label="Name" sortKey="talentId" currentSort={cSort} onSort={handleCSort} />
                      <SortFilterHeader label="Domain" sortKey="domain" currentSort={cSort} onSort={handleCSort} filterOptions={cFilterOpts.domains} filterValue={cFilters.domain} onFilter={(v) => handleCFilter('domain', v)} />
                      <SortFilterHeader label="Field" sortKey="field" currentSort={cSort} onSort={handleCSort} filterOptions={cFilterOpts.fields} filterValue={cFilters.field} onFilter={(v) => handleCFilter('field', v)} />
                      <SortFilterHeader label="Golden Talent" sortKey="isGolden" currentSort={cSort} onSort={handleCSort} filterOptions={['YES', 'NO']} filterValue={cFilters.golden} onFilter={(v) => handleCFilter('golden', v)} className="text-center" />
                      <SortFilterHeader label="Batches" sortKey="batch" currentSort={cSort} onSort={handleCSort} filterOptions={cFilterOpts.batches} filterValue={cFilters.batch} onFilter={(v) => handleCFilter('batch', v)} />
                      <SortFilterHeader label="Group" sortKey="groupName" currentSort={cSort} onSort={handleCSort} filterOptions={cFilterOpts.groups} filterValue={cFilters.groupName} onFilter={(v) => handleCFilter('groupName', v)} />
                      <SortFilterHeader label="Added by" sortKey="addedBy" currentSort={cSort} onSort={handleCSort} filterOptions={cFilterOpts.addedBys} filterValue={cFilters.addedBy} onFilter={(v) => handleCFilter('addedBy', v)} />
                      <SortFilterHeader label="Added at" sortKey="addedAt" currentSort={cSort} onSort={handleCSort} />
                      <th scope="col" className="px-4 py-3 font-semibold whitespace-nowrap">Approved by</th>
                      <th scope="col" className="px-4 py-3 font-semibold whitespace-nowrap">Approved at</th>
                      <th className="px-4 py-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y divide-gray-100 dark:divide-gray-800 transition-opacity ${cLoading ? 'opacity-40 pointer-events-none' : ''}`}>
                    {!cLoading && cProfiles.length === 0 && (
                      <tr>
                        <td colSpan={12} className="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                          No candidates match the current filters.
                        </td>
                      </tr>
                    )}
                    {(() => {
                      const allPageChecked = cProfiles.length > 0 && cProfiles.every((p) => cSelected.has(ckey(p.talentId, p.domain)));
                      if (!allPageChecked || cTotalPages <= 1) return null;
                      return (
                        <tr>
                          <td colSpan={12} className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-sm text-center border-b border-blue-100 dark:border-blue-800">
                            {cAllSelected ? (
                              <>
                                <span className="text-gray-700 dark:text-gray-300">All <strong>{cTotal}</strong> candidates selected.</span>
                                {' '}
                                <button onClick={clearCSelection} className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800">Clear selection</button>
                              </>
                            ) : (
                              <>
                                <span className="text-gray-700 dark:text-gray-300">All {cProfiles.length} on this page selected.</span>
                                {' '}
                                {cSelectAllLoading ? (
                                  <Loader2 className="w-3 h-3 inline animate-spin text-blue-500" />
                                ) : (
                                  <button onClick={handleCSelectAll} className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800">
                                    Select all {cTotal} candidates matching filters
                                  </button>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })()}
                    {cProfiles.map((p) => {
                      const key = ckey(p.talentId, p.domain);
                      return (
                        <tr key={key} onClick={() => { setSelectedProfile(p); setSelectedTab('view'); }} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group cursor-pointer">
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" checked={cSelected.has(key)} onChange={() => toggle(p, setCSelected, cSelectedMap)} className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500" />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="font-medium text-gray-900 dark:text-gray-100">{p.name || p.talentId}</div>
                            {p.name && <div className="text-xs text-gray-500 dark:text-gray-400">{p.talentId}</div>}
                          </td>
                          <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{p.domain}</span></td>
                          <td className="px-4 py-3">{p.field ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300">{p.field}</span> : <span className="text-gray-400 text-xs">—</span>}</td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><div className="flex justify-center"><GoldenStarButton talentId={p.talentId} initial={p.isGolden} disabled={cSelected.size > 0} onGoldenChange={(g) => syncCandidateGolden(p.talentId, p.domain, g)} /></div></td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {p.batches.length > 0 ? p.batches.map((b, i) => (
                                <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{b}</span>
                              )) : <span className="text-gray-400 text-xs">—</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {p.uploadGroup ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-800 max-w-[200px] truncate">{p.uploadGroup}</span> : <span className="text-gray-400 text-xs">—</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {p.addedBy ? (
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">{getInitials(p.addedBy)}</div>
                                <span className="text-sm text-gray-700 dark:text-gray-300">{p.addedBy}</span>
                              </div>
                            ) : <span className="text-gray-400 text-xs">—</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1">
                              {formatDate(p.addedAt)}
                              {isReingested(p.addedAt, p.reviewedAt) && (
                                <InfoTooltip text="Re-ingested after approval, so this timestamp reflects the latest ingestion and is later than the approval date." />
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap"><ActionedByCell name={p.reviewedBy} /></td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{p.reviewedAt ? formatDate(p.reviewedAt) : <span className="text-gray-400">—</span>}</td>
                          <td className="px-2 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={(e) => { e.stopPropagation(); setSelectedProfile(p); setSelectedTab('view'); }} className="p-1.5 text-gray-400 group-hover:text-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors" title="Quick View">
                                <Eye className="w-4 h-4" />
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); setSelectedProfile(p); setSelectedTab('edit'); }} className="p-1.5 text-gray-400 group-hover:text-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors" title="Edit Profile">
                                <Pencil className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {cLoading && <TableLoadingOverlay containerRef={cTableRef} />}
              </div>
            )}
            <Pagination page={cPage} totalPages={cTotalPages} total={cTotal} pageSize={cPageSize} onPageChange={(p) => { setCPage(p); cStateRef.current = { ...cStateRef.current, page: p }; fetchC({ page: p }); }} onPageSizeChange={(size) => { setCPageSize(size); setCPage(1); cStateRef.current = { ...cStateRef.current, pageSize: size, page: 1 }; fetchC({ page: 1, pageSize: size }); }} />
          </>
        )}

        {/* Rejected Table */}
        {activeTab === 'rejected' && (
          <>
            {!rShowTable && (
              <div className="p-6 text-center py-12">
                <CircleX className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">No rejected candidates</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">Candidates removed during review will appear here.</p>
              </div>
            )}
            {rShowTable && (
              <ReviewCandidatesTable
                profiles={rProfiles}
                loading={rLoading}
                total={rTotal}
                totalPages={rTotalPages}
                allSelected={rAllSelected}
                selectAllLoading={rSelectAllLoading}
                selected={rSelected}
                setSelected={setRSelected}
                selectedMap={rSelectedMap}
                sort={rSort}
                onSort={handleRSort}
                filters={rFilters}
                onFilter={handleRFilter}
                filterOpts={rFilterOpts}
                onSelectAllMatching={handleRSelectAll}
                onClearSelection={clearRSelection}
                onOpenProfile={(p, tab) => { setSelectedProfile(p); setSelectedTab(tab); }}
                containerRef={rTableRef}
                reviewedByLabel="Rejected by"
              />
            )}
            <Pagination page={rPage} totalPages={rTotalPages} total={rTotal} pageSize={rPageSize} onPageChange={(p) => { setRPage(p); rStateRef.current = { ...rStateRef.current, page: p }; fetchR({ page: p }); }} onPageSizeChange={(size) => { setRPageSize(size); setRPage(1); rStateRef.current = { ...rStateRef.current, pageSize: size, page: 1 }; fetchR({ page: 1, pageSize: size }); }} />
          </>
        )}
      </div>

      {/* Unified candidate detail modal (View + Edit + Fraud Check) */}
      {selectedProfile && (
        <CandidateUnifiedModal
          profile={selectedProfile}
          allProfiles={activeTab === 'candidates' ? cProfiles : activeTab === 'rejected' ? rProfiles : filteredIrProfiles}
          initialTab={selectedTab}
          onClose={() => setSelectedProfile(null)}
          onApprove={() => handleApprove([selectedProfile])}
          onRemove={() => handleReject([selectedProfile])}
          onNavigate={(p) => setSelectedProfile(p)}
          actionLoading={actionLoading}
          onEditorSaved={() => { invalidateCachePrefix('dev-'); fetchIR(); fetchC(); fetchR(); }}
        />
      )}

      {/* In Review Bulk Toast */}
      {irSelected.size > 0 && activeTab === 'in-review' && (
        <div className="fixed bottom-8 left-64 right-4 mx-auto w-fit max-w-[calc(100vw-17rem)] bg-gray-900 text-white px-4 py-3 rounded-xl shadow-xl flex flex-wrap items-center justify-center gap-2 animate-in slide-in-from-bottom-4 z-20">
          <span className="shrink-0 text-sm font-medium">{irSelected.size} candidate{irSelected.size !== 1 ? 's' : ''} selected</span>
          <button onClick={() => { setBulkEditTab('in-review'); setShowBulkEditModal(true); }} className="shrink-0 px-4 py-2 bg-white text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors">
            Bulk Edit
          </button>
          <button onClick={() => handleReject(getSelected('in-review'))} disabled={actionLoading} className="shrink-0 px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50">
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Remove'}
          </button>
          <button onClick={() => handleApprove(getSelected('in-review'))} disabled={actionLoading} className="shrink-0 px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50">
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Approve'}
          </button>
          <button onClick={() => exportPipelineCandidatesXlsx(getSelected('in-review'), 'in-review')} className="shrink-0 flex items-center gap-1.5 px-4 py-2 bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition-colors">
            <Download className="w-4 h-4" /> Export XLSX
          </button>
          <button onClick={clearIrSelection} className="p-1 text-gray-400 hover:text-white transition-colors" title="Clear selection">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Candidates Bulk Toast */}
      {cSelected.size > 0 && activeTab === 'candidates' && (
        <div className="fixed bottom-8 left-64 right-4 mx-auto w-fit max-w-[calc(100vw-17rem)] bg-gray-900 text-white px-4 py-3 rounded-xl shadow-xl flex flex-wrap items-center justify-center gap-2 animate-in slide-in-from-bottom-4 z-20">
          <span className="shrink-0 text-sm font-medium">{cSelected.size} candidate{cSelected.size !== 1 ? 's' : ''} selected</span>
          <button onClick={() => { setBulkEditTab('candidates'); setShowBulkEditModal(true); }} className="shrink-0 px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors">
            Bulk Edit
          </button>
          <button onClick={openBatchModal} className="shrink-0 px-4 py-2 bg-white text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors">
            Add to Batch
          </button>
          <GoldenBulkMenu
            candidates={getSelected('candidates')}
            onDone={() => { clearCSelection(); invalidateCachePrefix('admin-c'); fetchC(); }}
          />
          <button onClick={() => handleMoveToReview(getSelected('candidates'))} disabled={actionLoading} className="shrink-0 px-4 py-2 bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50">
            Move to In Review
          </button>
          <button onClick={() => exportPipelineCandidatesXlsx(getSelected('candidates'), 'candidates')} className="shrink-0 flex items-center gap-1.5 px-4 py-2 bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition-colors">
            <Download className="w-4 h-4" /> Export XLSX
          </button>
          <button onClick={clearCSelection} className="p-1 text-gray-400 hover:text-white transition-colors" title="Clear selection">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Rejected Bulk Toast */}
      {rSelected.size > 0 && activeTab === 'rejected' && (
        <div className="fixed bottom-8 left-64 right-4 mx-auto w-fit max-w-[calc(100vw-17rem)] bg-gray-900 text-white px-4 py-3 rounded-xl shadow-xl flex flex-wrap items-center justify-center gap-2 animate-in slide-in-from-bottom-4 z-20">
          <span className="shrink-0 text-sm font-medium">{rSelected.size} candidate{rSelected.size !== 1 ? 's' : ''} selected</span>
          <button onClick={() => handleRestoreToReview(getSelected('rejected'))} disabled={actionLoading} className="shrink-0 px-4 py-2 bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50">
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Move to In Review'}
          </button>
          <button onClick={() => exportPipelineCandidatesXlsx(getSelected('rejected'), 'rejected')} className="shrink-0 flex items-center gap-1.5 px-4 py-2 bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition-colors">
            <Download className="w-4 h-4" /> Export XLSX
          </button>
          <button onClick={clearRSelection} className="p-1 text-gray-400 hover:text-white transition-colors" title="Clear selection">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Add to Batch Modal */}
      {showBatchModal && (
        <AddToBatchModal
          ids={batchTargetIds}
          onClose={() => setShowBatchModal(false)}
          onDone={() => { setShowBatchModal(false); setCSelected(new Set()); cSelectedMap.current.clear(); fetchC(); }}
        />
      )}

      <ScrollButton />

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <BulkEditModal
          candidates={getSelected(bulkEditTab)}
          onClose={() => setShowBulkEditModal(false)}
          onDone={() => {
            setShowBulkEditModal(false);
            clearIrSelection(); clearCSelection();
            invalidateCachePrefix('admin-ir');
            invalidateCachePrefix('admin-c');
            fetchIR(); fetchC();
          }}
        />
      )}
    </div>
  );
}
